// Valletta, "Gold walls over Marsamxett": a warm, clear late-summer night on the Sliema Strand, at the ferry landing
// (35.9075 N, 14.5076 E), looking south-east (about 150°) across Marsamxett Harbour to Valletta, 0.9 km away. From
// there the view runs, left to right: Fort St Elmo low on the peninsula's tip (121°), the Marsamxett bastions and the
// city climbing behind them, the Carmelite basilica's square campanile and its great dome (153°), the spire of
// St Paul's Anglican Pro-Cathedral standing nearer, on the bastion's edge, to the dome's right, the high west bastions
// over the Marsamxett ferry quay, and Manoel Island with Fort Manoel (203°), nearer still. The spire's tip and the
// dome's cross stand at the same height from here (every photo from Sliema and Tigné agrees). The 90° is compressed
// into the frame without changing the order, and the city is drawn as a telephoto shot, the dome and the spire large.
// Back to front: deep blue sky, stars, a big moon -> Valletta's skyline: flat-roofed honey limestone blocks with
// painted gallariji, the Carmelite campanile and dome, the Pro-Cathedral's tower and spire, Fort St Elmo -> the
// bastions floodlit gold, the quay road's lamps -> Manoel Island and Fort Manoel -> Marsamxett (deep blue, mirrored
// gold), the Sliema ferry, luzzus moored at the Strand -> the Strand's kerb, railing, lamp and paving -> an old
// Sliema townhouse on the right (the cozy corner): a green gallarija lit from inside, a pastizzeria open on the
// street, a table outside, and the roof sign (the billboard).
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 163;                     // far waterline: the foot of Valletta's quay along Marsamxett
const KERB = 199;                   // top of the Strand's kerb: the harbour shows from WL down to it
const FEET = 205;                   // the Strand's people stand on this row (above the bottom 63 rows)
const MOON = { x: 352, y: 30, r: 6 };
// The Carmelite basilica's dome (1958-81). Measured on two night photos from Sliema and Tigné: the visible dome, from
// the drum's foot behind the roofs to the cross, is 1.31 times the drum's width; the lantern, its cap and the cross
// take 0.41 of that height, the shell 0.32, the drum and its balustrade 0.27.
const DOME = { cx: 204, tip: 34, base: 92, hw: 22 };   // cross tip, drum foot (behind the roofs), drum half-width
// St Paul's Pro-Cathedral (1844): the tower and its octagonal spire are 64.5 m, the spire 25 m of it (0.39); the
// tower is 0.42 of the dome's drum wide, and its spire's tip is level with the dome's cross from the Strand.
const SPIRE = { cx: 268, tip: 34, top: 70, foot: 126, hw: 9 };
const CAMP = { cx: 174, top: 68, hw: 6 };             // the Carmelite campanile, left of the dome: its top at 0.58 down
const BASTION = 134;                                   // top of the bastions' parapet; their face runs down to QUAY
const QUAY = 157;                                      // the quay road along the water under the bastions
const HOUSE = { x0: 388, roof: 120, floor: 206 };      // the Sliema townhouse (cozy corner), to the right edge
const BB = { x: 396, y: 94, w: 50, h: 20 };            // its roof sign: the billboard's face
const LAMP = { x: 34, y: 140 };                        // the Strand's lamp (left, near the viewer)
const MANOEL = { x0: 318, wl: 168 };                   // Manoel Island's tip and its waterline (nearer than Valletta's)

/* ================= Pixel helpers ================= */
// ImageData painter for pixel-heavy layers
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, get, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], t)).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
// pick from a dark-to-light ramp with ordered dither
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}

/* ================= Sign font: FONT3 with wide M, W, N, K, V (FONT3's read as H and Π at 1x) ================= */
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y), col); cx += (g?.[0].length ?? 3) + 1; }
  return cx - x - 1;
}

/* ================= Sky: deep Mediterranean blue, a violet haze over the city's lights ================= */
const SKY = ['#030616', '#050a1f', '#070e28', '#0a1331', '#0d183a', '#111d44', '#16234d', '#1c2a56', '#23305c',
  '#2c3661', '#383c66', '#454268', '#544867', '#634e64'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 160), 1.5) * 0.84;
    v += Math.max(0, 1 - Math.hypot((i - 200) / 260, (y - 150) / 80)) * 0.2;      // Valletta's lights on the haze
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 80, (y - MOON.y) / 55)) * 0.12; // moonlit sky round the moon
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -1, 1.6], [1, 2, 1.4], [2, -2, 1], [-1, 3, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -4 ? '#fffbea' : '#f2ead2';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#c8bc9e' : '#e6dcc0';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.1 ? '#bfb498' : '#d6ccb0';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(52);
  while (STARS.length < 54) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 84);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16) continue;
    STARS.push({ x, y, k: 12 + Math.floor(r() * 50), ph: r(), b: y < 50 ? 0.45 + r() * 0.5 : 0.22 + r() * 0.3, big: r() < 0.08 });
  }
}
// two thin moonlit wisps, dithered into the sky with no outline; tileable so they can drift
const WISPS = [{ seed: 7, y: 8, h: 14, n: 2, k: 1 }, { seed: 21, y: 30, h: 14, n: 1, k: 2 }, { seed: 13, y: 50, h: 14, n: 2, k: 1 }, { seed: 29, y: 70, h: 14, n: 1, k: 1 }];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < def.n; b++) {
    const cx = r() * W, len = 50 + r() * 60;
    for (let k = 0; k < 5; k++) blobs.push({ x: cx + (r() - 0.5) * len, y: h * (0.25 + r() * 0.5), rx: 8 + r() * 14, ry: 1.2 + r() * 1.4 });
  }
  const p = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) {
      let dx = x - b.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    }
    if (v > 0.6) p.put(x, y, v > 0.82 ? '#26305c' : '#1e2854');
    else if (v > 0.22 && (v - 0.22) * 2.4 > bayer(x, y)) p.put(x, y, '#18214c');
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Valletta: flat-roofed limestone blocks climbing from the bastions ================= */
// Seen from Sliema the city is a long bank of four- to six-storey houses, flat-roofed with parapets and roof rooms, their
// honey limestone lit gold by the street lamps, every few houses a painted wooden gallarija (an enclosed balcony) on
// the upper floors. It climbs from Fort St Elmo on the left to the ridge under the dome and the spire, then runs on
// level to the high west bastions.
const NIGHT = '#101634', CNIGHT = '#1a1424';                           // the sky's night; the city's warm shadow
const LIME = ['#f8d898', '#e4b872', '#c89650', '#9a6c3a', '#6a482a'];      // limestone, lamplit to shadowed
const GAL = ['#3c8a4e', '#a8323a', '#2e62a8', '#7a4a2a', '#2e7a7a'];     // gallarija paint: green, red, blue, brown, teal
const WARMW = ['#ffe6a8', '#f8c470', '#e89a50'];
const RIDGE = [[36, 126], [60, 117], [96, 108], [128, 101], [156, 96], [190, 93], [236, 95], [280, 96], [320, 99], [392, 102]];
const ridgeY = x => { for (let k = 1; k < RIDGE.length; k++) { const [a, ya] = RIDGE[k - 1], [b, yb] = RIDGE[k]; if (x <= b) return lerp(ya, yb, clamp((x - a) / (b - a), 0, 1)); } return 102; };
// tiers back to front: [x0, x1, how far under the ridge the tops sit, base row, storey height, night mix, widths]
const TIERS = [
  { x0: 40, x1: 392, drop: [0, 5], base: 118, st: 5, dk: 0.4, wd: [9, 9], seed: 11 },
  { x0: 36, x1: 392, drop: [8, 13], base: 128, st: 5, dk: 0.2, wd: [11, 12], seed: 23 },
  { x0: 44, x1: 392, drop: [17, 22], base: BASTION, st: 6, dk: 0.04, wd: [13, 14], seed: 37 },
];
const BLOCKS = [];
function layoutCity() {
  TIERS.forEach((T, ti) => {
    const r = rng(T.seed);
    let x = T.x0, prev = -1, prevTop = 0;
    while (x < T.x1) {
      const w = T.wd[0] + Math.floor(r() * T.wd[1]);
      let shade; do shade = Math.floor(r() * 4); while (shade === prev);
      prev = shade;
      let top = Math.round(ridgeY(x + w / 2) + T.drop[0] + r() * (T.drop[1] - T.drop[0]));
      // the roofs that hide the drum's foot all stop one row under it, so the dome shows down to DOME.base
      if (ti === 0 && x < DOME.cx + DOME.hw + 3 && x + w > DOME.cx - DOME.hw - 3) top = DOME.base + 1 + Math.floor(r() * 2);
      BLOCKS.push({ x, w: Math.min(w, T.x1 + 4 - x), top, base: T.base, ti, st: T.st, dk: T.dk, shade, seed: x * 7 + ti * 131,
        edge: top < prevTop, room: r() < 0.3, rx: Math.floor(r() * 6), gal: r() < 0.5 ? Math.floor(r() * GAL.length) : -1,
        galFloor: 1 + Math.floor(r() * 2), galAt: Math.floor(r() * 3) });
      prevTop = top;
      x += w;
    }
  });
}
// a block of flats or a palazzo: coping, a string course, columns of tall windows (lit, shuttered or dark), now and
// then a gallarija over two or three window columns; lit from the street, so darker towards the roof
function drawBlock(p, b) {
  const put = p.put, { x, w, top, base, st } = b, front = b.ti === 2;
  const L = LIME.map(c => mixHex(c, CNIGHT, b.dk + b.shade * 0.05));
  const win = WARMW.map(c => mixHex(c, '#40202a', b.dk * 0.45)), dark = mixHex('#1c1626', CNIGHT, b.dk * 0.5);
  const shut = mixHex(['#3e6040', '#62402a', '#3e4e60'][b.seed % 3], CNIGHT, b.dk + 0.12);
  for (let y = top; y < base; y++) for (let i = x; i < x + w; i++) {
    const v = 0.22 + (base - y) / (base - top + 8) * 0.5 + (bayer(i, y) - 0.5) * 0.1 + b.shade * 0.04;
    let col = ramp(L, v, i, y);
    if (i === x && b.edge) col = L[0];                            // a lit corner where it stands above its neighbour
    if (i === x + w - 1) col = L[3];
    if (y === top) col = L[0];                                    // the parapet's coping
    if (y === top + 1) col = L[3];
    put(i, y, col);
  }
  // a roof room or a water tank on the flat roof
  if (b.room && w >= 10 && !(b.ti === 0 && x < DOME.cx + DOME.hw + 3 && x + w > DOME.cx - DOME.hw - 3)) { const rx = x + 2 + b.rx % (w - 7); for (let y = top - 3; y < top; y++) for (let i = rx; i < rx + 5; i++) put(i, y, y === top - 3 ? L[1] : i === rx + 4 ? L[4] : L[2]); }
  const floors = Math.floor((base - top - 3) / st), cols = [];
  for (let i = x + 2; i < x + w - 2; i += 3) cols.push(i);
  for (let f = 0; f < floors; f++) {
    const wy = top + 3 + f * st;
    if (wy + 2 >= base) break;
    const gal = b.gal >= 0 && f === Math.min(b.galFloor, floors - 1) && cols.length >= 3;
    const g0 = gal ? cols[Math.min(b.galAt, cols.length - 3)] - 1 : 0, g1 = g0 + 7;
    if (gal) {                                                    // the enclosed wooden balcony, its panes lit, on its corbels
      const g = mixHex(GAL[b.gal], CNIGHT, b.dk * 0.7), gd = mixHex(GAL[b.gal], '#000000', 0.5);
      for (let i = g0; i <= g1; i++) {
        put(i, wy - 1, g); put(i, wy + 2, gd);
        const pane = (i - g0) % 2 === 1;
        put(i, wy, pane ? (hash(b.seed, i, f) < 0.65 ? win[1] : mixHex('#7a9ab8', CNIGHT, b.dk)) : g);
        put(i, wy + 1, pane ? (hash(b.seed, i, f + 9) < 0.5 ? win[2] : gd) : g);
      }
      put(g0, wy + 3, L[3]); put(g1, wy + 3, L[3]);
    }
    for (const i of cols) {
      if (gal && i >= g0 && i <= g1) continue;
      const hsh = hash(b.seed, i, f * 3 + 1);
      const col = hsh < 0.3 ? win[0] : hsh < 0.46 ? win[1] : hsh < 0.78 ? shut : dark;
      put(i, wy, col); put(i, wy + 1, col);
      if (front) put(i, wy + 2, hsh < 0.38 ? mixHex(col, L[2], 0.5) : col);
    }
  }
}
// Fort St Elmo on the peninsula's tip: low star-fort walls floodlit pale, barracks on top
function renderElmo(p) {
  const put = p.put;
  for (let x = 2; x < 52; x++) {
    const top = 137 + Math.round(Math.max(0, 22 - x) * 0.18), salient = x === 20 || x === 38;
    for (let y = top; y < QUAY + 2; y++) {
      const v = 0.25 + (y - top) / (QUAY + 2 - top) * 0.5 + (bayer(x, y) - 0.5) * 0.2;
      put(x, y, y === top ? '#e8c890' : y === top + 2 ? '#d8b47a' : salient ? '#f0d098' : ramp(['#5e4a36', '#8a6c46', '#b48c58', '#d4a86a'], v, x, y));
    }
  }
  for (const [bx, bw, bt] of [[8, 12, 129], [24, 9, 131], [36, 11, 128]]) {
    for (let y = bt; y < 137; y++) for (let i = bx; i < bx + bw; i++) put(i, y, y === bt ? '#cdb084' : i === bx ? '#b89a70' : '#7e664c');
    for (let i = bx + 2; i < bx + bw - 1; i += 3) put(i, bt + 3, hash(bx, i) < 0.4 ? '#f2c878' : '#3a3040');
  }
}
// the high west bastions (St Andrew's and St Michael's) with Hastings Gardens' trees on top, and Floriana's two
// belfries far off over them
function renderWestBastion(p) {
  const put = p.put;
  for (const tx of [347, 353]) for (let y = 98; y < 112; y++) for (let i = tx; i < tx + 3; i++) put(i, y, y < 100 ? (i === tx + 1 ? '#a08a6a' : null) : i === tx ? '#a08868' : '#6e5c48');
  const r = rng(91);
  for (let k = 0; k < 16; k++) {
    const cx = 318 + r() * 70, cy = 112 + r() * 3, rx = 3 + r() * 4, ry = 2 + r() * 2;
    for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let i = Math.floor(cx - rx); i <= cx + rx; i++)
      if (((i - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1) put(i, y, (y - cy) < -ry * 0.3 && bayer(i, y) > 0.4 ? '#3a4a30' : '#1e2a20');
  }
}

/* ================= The Carmelite dome: drum, shell, lantern, floodlit cream and gold ================= */
// rows: cross 34-39, ball 40-41, lantern cap 42-44, lantern cornice 45, lantern 46-56, its base 57-58, the ribbed shell
// 59-76, the drum's balustrade 77-79, the drum with its ring of lit arched windows 80-92 (its foot behind the roofs).
// The drum and the lantern are the brightest stone in the frame; the shell is lead-grey over them, lit from below, so
// it darkens towards the lantern, its ribs catching the light.
const DM = { hi: '#fff8e6', lt: '#f8e6c0', md: '#e2c99a', sh: '#b8a07a', dk: '#82705a', ink: '#3a3034', glow: '#fff4cc' };
const SHELL = ['#3c3c48', '#50505c', '#66656e', '#7c7a7e', '#94908c', '#aca59a', '#c2b8a6'];   // the lead-grey shell, warm low down
function renderDome() {
  const p = painter(W, H), put = p.put, cx = DOME.cx, hw = DOME.hw;
  // the cross and the ball
  for (let y = 34; y <= 39; y++) put(cx, y, y < 36 ? '#e8dcc0' : '#d4c4a2');
  put(cx - 1, 36, '#d8cab0'); put(cx + 1, 36, '#b8a888');
  put(cx - 1, 40, DM.lt); put(cx, 40, DM.hi); put(cx + 1, 40, DM.md); put(cx, 41, DM.md);
  // the lantern's ribbed cap
  [[42, 2], [43, 3], [44, 4]].forEach(([y, h2]) => { for (let i = cx - h2; i <= cx + h2; i++) put(i, y, i === cx - h2 ? '#b8b0a4' : i - cx > h2 - 2 ? '#6a6870' : (i - cx) % 2 ? '#8a8688' : '#a6a09a'); });
  // the lantern: a cornice, four piers and three lit arched openings, a balustraded base
  for (let i = cx - 6; i <= cx + 6; i++) put(i, 45, i < cx ? DM.hi : DM.lt);
  for (let y = 46; y <= 56; y++) for (let i = cx - 5; i <= cx + 5; i++) {
    const pier = i === cx - 5 || i === cx - 2 || i === cx + 2 || i === cx + 5;
    const open = !pier && y >= 48 && y <= 54 && !(y === 48 && (i === cx - 4 || i === cx + 4 || i === cx - 1 || i === cx + 1));
    put(i, y, open ? (y <= 50 ? DM.glow : '#ffe6a8') : pier ? (i < cx ? DM.lt : i === cx + 5 ? DM.sh : DM.md) : (y === 47 || y === 55 ? DM.md : DM.lt));
  }
  for (let i = cx - 7; i <= cx + 7; i++) { put(i, 57, i < cx + 3 ? DM.hi : DM.lt); put(i, 58, (i & 1) ? DM.md : DM.sh); }
  // the shell: a slightly flattened half-ellipse, 16 stone ribs, lit from below
  const yS = 76, yT = 59, R = hw - 1.5;
  for (let y = yT; y <= yS; y++) {
    const q = (y - (yS + 0.5)) / (yS + 0.5 - yT + 0.6), half = R * Math.sqrt(Math.max(0, 1 - q * q));
    for (let i = Math.round(cx - half); i <= Math.round(cx + half); i++) {
      const s = clamp((i - cx) / Math.max(1, half), -1, 1), th = Math.asin(s);
      const rib = Math.abs(frac(th / (TAU / 16) + 0.5) - 0.5) < 0.09 * (1 + Math.abs(s));
      let v = 0.9 - (yS - y) / (yS - yT) * 0.45 - s * s * 0.3 - s * 0.08 + (bayer(i, y) - 0.5) * 0.08;
      if (rib) v += 0.22;
      put(i, y, ramp(SHELL, v, i, y));
    }
  }
  // the drum: balustrade, entablature, paired pilasters and a ring of 24 arched windows (ten in view), lit inside
  for (let i = cx - hw - 1; i <= cx + hw + 1; i++) { put(i, 77, i < cx + 10 ? DM.hi : DM.lt); put(i, 78, (i & 1) ? DM.lt : DM.sh); put(i, 79, DM.md); }
  const wins = [];
  for (let k = 0; k < 5; k++) { const a = (k + 0.5) * TAU / 24, o = hw * Math.sin(a), wide = Math.cos(a) > 0.62; wins.push([Math.round(cx - o), wide], [Math.round(cx + o), wide]); }
  for (let y = 80; y <= DOME.base + 8; y++) for (let i = cx - hw; i <= cx + hw; i++) {
    const s = (i - cx) / hw, v = 0.95 - s * s * 0.45 - s * 0.1 + (bayer(i, y) - 0.5) * 0.12;
    put(i, y, y === 80 ? DM.lt : y === 81 ? DM.sh : ramp(['#8a7052', '#a88a62', '#c4a474', '#dcbc88', '#ecd09c'], v, i, y));
  }
  const isWin = new Set();
  for (const [wx, wide] of wins) for (let i = wx; i < wx + (wide ? 2 : 1); i++) isWin.add(i);
  for (const [wx, wide] of wins) for (const i of [wx - 1, wx + (wide ? 2 : 1)]) {
    if (isWin.has(i) || Math.abs(i - cx) > hw - 1) continue;
    for (let y = 82; y <= 91; y++) put(i, y, y === 82 ? DM.hi : i < cx ? '#f6e0b0' : '#e8cc98');
    const mid = i + (i < wx ? -1 : 1);
    if (!isWin.has(mid) && Math.abs(mid - cx) < hw - 1 && !wins.some(([w2]) => Math.abs(w2 - mid) === 1 && w2 !== wx))
      for (let y = 83; y <= 91; y++) put(mid, y, '#a88a62');
  }
  for (const [wx, wide] of wins) for (let y = 83; y <= 90; y++) {
    const edge = Math.abs(wx - cx) > hw - 3;
    for (let i = wx; i < wx + (wide ? 2 : 1); i++) if (!(y === 83 && wide && i === wx + 1)) put(i, y, edge ? '#f0d49a' : y < 86 ? '#fffae8' : '#ffecb8');
  }
  return p.done();
}
// the Carmelite campanile: a plain square tower, its top at the shell's middle, an arched belfry opening each stage
function renderCampanile(p) {
  const put = p.put, { cx, top, hw } = CAMP;
  const C = ['#f0d8a8', '#d6ba86', '#b09468', '#80684c'];
  for (let y = top; y < 104; y++) for (let i = cx - hw; i <= cx + hw; i++) {
    const v = (i - cx + hw) / (2 * hw) + (bayer(i, y) - 0.5) * 0.2 - (y - top) / 60;
    put(i, y, y === top ? C[0] : y === top + 1 ? C[2] : y === top + 14 ? C[1] : i === cx - hw ? C[0] : i === cx + hw ? C[3] : ramp([C[0], C[1], C[2]], v, i, y));
  }
  for (let i = cx - hw - 1; i <= cx + hw + 1; i++) put(i, top + 1, i < cx ? C[0] : C[1]);
  for (const [y0, y1] of [[top + 4, top + 11], [top + 17, top + 23]]) for (let y = y0; y <= y1; y++) for (let i = cx - 1; i <= cx + 1; i++)
    if (!(y === y0 && i !== cx)) put(i, y, y === y0 + 1 && i === cx ? '#5a4436' : '#2e2430');
}
// the small pale dome on a roof at the big dome's left foot (a chapel's), in front of the drum
function renderPaleDome(p) {
  const cx = 182, base = 95, r = 6, ry = 5, pal = ['#8a8478', '#b4ac9c', '#d8d0bc', '#ece4d0'];
  for (let y = base - ry; y <= base + 6; y++) for (let i = cx - r - 1; i <= cx + r + 1; i++) {
    if (y > base) { p.put(i, y, i === cx - r - 1 ? '#c8ac80' : '#8e7454'); continue; }
    const q = ((i - cx) / (r + 0.4)) ** 2 + ((y - base) / (ry + 0.4)) ** 2;
    if (q < 1) p.put(i, y, ramp(pal, 0.85 - (i - cx) / r * 0.3 - (base - y) / ry * 0.25, i, y));
  }
}
// the small pale dome at the big dome's left foot, and a lesser church dome and a square tower on the ridge to the left
function renderLesserDomes(p) {
  const put = p.put;
  const smallDome = (cx, base, r, ry, pal) => {
    for (let y = base - ry; y <= base; y++) for (let i = cx - r; i <= cx + r; i++) {
      const q = ((i - cx) / (r + 0.4)) ** 2 + ((y - base) / (ry + 0.4)) ** 2;
      if (q < 1) put(i, y, ramp(pal, 0.85 - (i - cx) / r * 0.3 - (base - y) / ry * 0.25, i, y));
    }
  };
  // a lesser church on the ridge: drum, dome, lantern
  const lx = 112;
  for (let y = 96; y < 106; y++) for (let i = lx - 5; i <= lx + 5; i++) put(i, y, i === lx - 5 ? '#d8bc88' : i > lx + 3 ? '#8a6e4c' : (i - lx) % 3 === 0 && y > 98 && y < 103 ? '#f2c878' : '#b89a6a');
  smallDome(lx, 95, 5, 5, ['#6e604c', '#9a8462', '#c2a87c', '#dcc498']);
  for (let y = 87; y <= 90; y++) put(lx, y, y === 87 ? '#c8b08a' : '#a8906c');
  // a square tower with a signal mast further left
  const tx = 142;
  for (let y = 88; y < 104; y++) for (let i = tx - 3; i <= tx + 3; i++) put(i, y, y === 88 ? '#d8c090' : i === tx - 3 ? '#cdb282' : i === tx + 3 ? '#7a6248' : y === 92 && i === tx ? '#3a2e30' : '#a88c62');
  for (let y = 80; y < 88; y++) put(tx, y, '#5a5058');
  put(tx - 1, 83, '#5a5058'); put(tx + 1, 83, '#5a5058');
}

/* ================= St Paul's Pro-Cathedral: the tower and its octagonal spire, honey limestone ================= */
// rows: spire 34-69 (0.39 of the 92 rows from the tip to the tower's foot), pinnacles 63-69, the balustrade 70-72, the
// belfry stage with its tall louvred opening 73-88, a cornice, the oculus stage 91-104, a cornice, the lowest stage
// with a round and an arched window 106-126. Floodlit from the bastion below, a step under the dome.
const SP = { hi: '#ffe8b8', lt: '#f2cc8e', md: '#d4a86a', sh: '#a47c4c', dk: '#6e5036', ink: '#2e2228' };
function renderSpire() {
  const p = painter(W, H), put = p.put, { cx, tip, top, foot, hw } = SPIRE;
  // the spire: three facets of the octagon in view, the left lit, two rows of small lucarnes
  for (let y = tip; y < top; y++) {
    // a symmetric taper: the whole pixels of each row, and where the true edge is past half a pixel, a half-tone
    // pixel each side, so the octagon tapers evenly instead of in steps
    const half = (y - tip) / (top - 1 - tip) * 4.6, core = Math.floor(half), f = half - core;
    for (let i = cx - core; i <= cx + core; i++) {
      const s = core ? (i - cx) / core : 0;
      const col = s < -0.4 ? SP.lt : s > 0.4 ? SP.sh : SP.md;
      put(i, y, y === tip ? SP.lt : (i === cx - core && core > 0 && y > tip + 3) ? SP.hi : col);
    }
    if (f > 0.35 && y > tip) { put(cx - core - 1, y, mixHex(SP.lt, '#1c2250', f > 0.7 ? 0.25 : 0.55)); put(cx + core + 1, y, mixHex(SP.sh, '#1c2250', f > 0.7 ? 0.3 : 0.6)); }
  }
  for (const ly of [49, 60]) { put(cx, ly, SP.ink); put(cx, ly + 1, SP.ink); put(cx, ly - 1, SP.lt); put(cx - 1, ly + 1, SP.hi); }
  // pinnacles at the tower's corners and small ones at the spire's foot
  for (const [px0, h2, w2] of [[cx - hw, 6, 2], [cx + hw - 1, 6, 2], [cx - hw + 3, 5, 1], [cx + hw - 3, 5, 1], [cx - 5, 3, 1], [cx + 5, 3, 1]]) {
    for (let y = top - h2; y < top; y++) for (let k = 0; k < w2; k++) put(px0 + k, y, px0 < cx ? (k ? SP.md : SP.lt) : (k ? SP.sh : SP.md));
    put(px0 + (w2 > 1 && px0 > cx ? 1 : 0), top - h2 - 1, px0 < cx ? SP.hi : SP.lt);              // the urn on top
  }
  for (let i = cx - 4; i <= cx + 4; i++) put(i, top - 1, i < cx ? SP.hi : SP.lt);                 // the spire's collar
  // the balustrade
  for (let i = cx - hw - 1; i <= cx + hw + 1; i++) { put(i, top, i < cx + 3 ? SP.hi : SP.lt); put(i, top + 1, (i & 1) ? SP.lt : SP.dk); put(i, top + 2, SP.md); }
  // the tower: corner pilasters, floodlight rising from below, cornices between the stages
  for (let y = top + 3; y <= foot; y++) for (let i = cx - hw; i <= cx + hw; i++) {
    const u = (i - (cx - hw)) / (2 * hw), v = 0.35 + (y - top) / (foot - top) * 0.45 - u * 0.4;
    let col = ramp(['#b88c58', '#c89a60', '#d8a96a', '#e6b878', '#f0c888', '#f8d89c'], v, i, y);
    if (i === cx - hw) col = SP.hi; else if (i === cx + hw) col = SP.dk; else if (i === cx - hw + 1) col = SP.lt; else if (i === cx + hw - 1) col = SP.sh;
    put(i, y, col);
  }
  for (const cy of [89, 105]) for (let i = cx - hw - 1; i <= cx + hw + 1; i++) { put(i, cy, i <= cx ? SP.hi : SP.lt); put(i, cy + 1, SP.sh); }
  // the belfry's tall louvred arch, the oculus, a round window and an arched window
  for (let y = 76; y <= 86; y++) for (let i = cx - 1; i <= cx + 1; i++) if (!(y === 76 && i !== cx)) put(i, y, y % 2 && y > 78 ? '#5a4236' : SP.ink);
  for (const [i, y] of [[0, -1], [-1, 0], [1, 0], [0, 1]]) put(cx + i, 96 + y, SP.dk);
  put(cx, 96, '#f2d498');
  for (const [i, y] of [[0, -1], [-1, 0], [1, 0], [0, 1]]) put(cx + i, 110 + y, SP.dk);
  for (let y = 115; y <= 121; y++) for (let i = cx - 1; i <= cx + 1; i++) if (!(y === 115 && i !== cx)) put(i, y, y < 118 ? '#e8b464' : '#c88a48');
  // the church's body to the tower's right: the nave's lit flank, a cornice, tall round-headed windows lit within
  for (let y = 104; y <= foot; y++) for (let i = cx + hw + 1; i <= cx + hw + 22; i++) {
    let col = y === 104 ? SP.lt : y === 105 ? SP.md : y === 106 ? SP.sh : ramp([SP.sh, SP.md, SP.lt], 0.25 + (y - 104) / 30 + (bayer(i, y) - 0.5) * 0.08, i, y);
    if (i === cx + hw + 22) col = SP.dk;
    const k = (i - cx - hw - 3) % 6;
    if (k >= 0 && k < 2 && i < cx + hw + 21 && y >= 110 && y <= 120) col = y === 110 ? SP.md : y < 114 ? '#f6c878' : '#e0a050';
    put(i, y, col);
  }
  return p.done();
}

/* ================= The bastions: battered walls rising from the quay, floodlit gold ================= */
// salients (the bastions' points) every so often, each with a lit edge leaning with the wall's batter; the cordon
// three rows under the parapet; floodlights on the quay road light the wall from below; the high west bastions stand
// taller on the right
const SALIENTS = [74, 128, 186, 238, 296, 336];
const bastionTop = x => x >= 318 ? 118 : BASTION;
const QLAMPS = [52, 73, 96, 119, 141, 167, 189, 213, 237, 261, 284, 306];
function renderBastions(p) {
  const put = p.put, B = ['#4e3420', '#6e4a2a', '#946634', '#bc8840', '#dcaa58', '#f2ca7c'];
  for (let x = 44; x < HOUSE.x0 + 4; x++) {
    const top = bastionTop(x);
    for (let y = top; y < QUAY; y++) {
      const depth = (y - top) / (QUAY - top), lean = Math.floor((y - top) / 6);
      const sal = SALIENTS.some(s => x === s + lean), salR = SALIENTS.some(s => x > s + lean && x <= s + lean + 4);
      const pool = QLAMPS.reduce((a, q) => Math.max(a, 1 - Math.abs(x - q) / 16), 0) * depth * 0.18;
      let v = 0.3 + depth * 0.5 + pool + (bayer(x, y) - 0.5) * 0.1 + (salR ? -0.14 : 0);
      let col = ramp(B, v, x, y);
      if (y === top) col = '#f4d498';
      else if (y === top + 1) col = '#8e6236';
      else if (y === top + 3) col = '#e2b468';                                   // the cordon
      else if (y === top + 4) col = '#a87840';
      if (sal && y > top) col = '#ffdca0';
      put(x, y, col);
    }
  }
  // the quay road: a lit kerb, dark asphalt, the sea wall's coping catching the lamps
  for (let x = 40; x < HOUSE.x0 + 4; x++) for (let y = QUAY; y < WL; y++)
    put(x, y, y === QUAY ? '#5a4234' : y === WL - 1 ? '#c09060' : y === WL - 2 ? '#6a5040' : ramp(['#1e1a24', '#2a242c', '#3a3034'], QLAMPS.reduce((a, q) => Math.max(a, 1 - Math.abs(x - q) / 9), 0), x, y));
}

/* ================= Manoel Island and Fort Manoel, nearer than Valletta, on the right ================= */
function renderManoel() {
  const p = painter(W, H), put = p.put, { x0, wl } = MANOEL;
  const top = x => wl - 6 - Math.min(12, (x - x0) * 0.4) + 1.5 * Math.sin(x * 0.3);
  for (let x = x0; x < HOUSE.x0 + 4; x++) {
    const t0 = Math.round(top(x));
    for (let y = t0; y <= wl; y++) put(x, y, y === wl ? '#2a2a34' : ramp(['#0e141e', '#141c26', '#1a242c'], (y - t0) / 14 + (bayer(x, y) - 0.5) * 0.3, x, y));
  }
  // Fort Manoel's walls along the shore, dimly floodlit, its gate's lamp
  for (let x = x0 + 14; x < HOUSE.x0 + 4; x++) for (let y = wl - 9; y < wl - 1; y++) {
    const v = 0.3 + (y - (wl - 9)) / 8 * 0.4 + (bayer(x, y) - 0.5) * 0.2;
    put(x, y, y === wl - 9 ? '#e0b070' : ramp(['#5a4230', '#86623a', '#b08450', '#d0a060'], v, x, y));
  }
  for (const gx of [352, 374]) { put(gx, wl - 6, '#ffd890'); put(gx, wl - 5, '#3a2a24'); put(gx, wl - 4, '#3a2a24'); }
  // trees over the fort
  const r = rng(63);
  for (let k = 0; k < 12; k++) {
    const cx = x0 + 8 + r() * 66, cy = wl - 12 - r() * 5, rx = 3 + r() * 3, ry = 2 + r() * 2;
    for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let i = Math.floor(cx - rx); i <= cx + rx; i++)
      if (((i - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 < 1) put(i, y, (y - cy) < -ry * 0.3 && bayer(i, y) > 0.5 ? '#26321e' : '#121a14');
  }
  return p.done();
}

/* ================= Marsamxett: deep blue water ================= */
const renderWater = () => ditherFill(W, KERB - WL + 4, ['#0e2a48', '#0b223e', '#091b34', '#07152a', '#051020'], (i, y) => y / (KERB - WL + 4) * 0.85 + 0.1);

/* ================= Boats: luzzus moored at the Strand, the Sliema ferry ================= */
// a luzzu: double-ended, its stem and sternpost standing proud, painted in bands (yellow gunwale, red, blue, a green
// and a yellow stripe), the eye of Osiris on the bow
const LUZZU_ROWS = [
  'Y..............................y',
  'Yr............................ry',
  '.Yr..........................ry.',
  '.YYYYYYYYYYYYYYYYYYYYYYYYYYYYYy.',
  '..RRRRRRRRRRRRRRRRRRRRRRRRRRRr..',
  '..BekBBBBBBBBBBBBBBBBBBBBBBBBb..',
  '...BBBBBBBBBBBBBBBBBBBBBBBBBb...',
  '...bBBBBBBBBBBBBBBBBBBBBBBBBb...',
  '....GGGGGGGGGGGGGGGGGGGGGGGg....',
  '.....wwwwwwwwwwwwwwwwwwwwww.....',
];
const night = (c, k) => mixHex(c, NIGHT, k);
const LUZZU_KEY = { Y: night('#f0c430', 0.25), y: night('#c89a20', 0.35), R: night('#d23a2e', 0.25), r: night('#8a2a26', 0.4),
  B: night('#2a6ab8', 0.25), b: night('#1e4a88', 0.35), G: night('#2e9a5a', 0.3), g: night('#1e6a3e', 0.4), e: '#f0ece0', k: '#14141c',
  w: night('#d8d4c8', 0.45) };
// the ferry: a white catamaran, its saloon lit, an open upper deck with a rail
const FERRY_ROWS = [
  '...........kkkkkkkkkkkkkkkkkkkkk..........',
  '...........k.k.k.k.k.k.k.k.k.k.k..........',
  '..........wwwwwwwwwwwwwwwwwwwwwwwwk.......',
  '........wwyyykyyykyyykyyykyyykyyywwk......',
  '.......wwwyyykyyykyyykyyykyyykyyywww......',
  '....wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwww..',
  '..wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwk.',
  '.wwBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBw.',
  '..wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwk..',
  '...ssssssss....................ssssssss...',
];
const FERRY_KEY = { w: '#d0d6e0', B: '#2a5aa0', y: '#ffe2a0', k: '#3a3e4c', s: '#2a2e3a' };
// the ferry at a middle and a far distance (the far one is symmetric, so it needs no turning at Valletta's landing)
const FERRY_MID = [
  '........kkkkkkkkkkkkkk........',
  '.......wwwwwwwwwwwwwwwwk......',
  '.....wwyyykyyykyyykyyyww......',
  '...wwwwwwwwwwwwwwwwwwwwwwwww..',
  '.wwBBBBBBBBBBBBBBBBBBBBBBBBBw.',
  '..wwwwwwwwwwwwwwwwwwwwwwwwwk..',
  '...sssss..............sssss...',
];
const FERRY_FAR = ['.....kkkkkkkk.....', '...wwyyyyyyyyww...', '.wwwwwwwwwwwwwwww.', '.wBBBBBBBBBBBBBBw.', '..sss........sss..'];
const LUZZU_MID = [
  'Y......................y',
  '.YYYYYYYYYYYYYYYYYYYYYy.',
  '..RRRRRRRRRRRRRRRRRRRr..',
  '..BekBBBBBBBBBBBBBBBBb..',
  '...BBBBBBBBBBBBBBBBBb...',
  '...GGGGGGGGGGGGGGGGGg...',
  '....wwwwwwwwwwwwwwww....',
];
// a harbour-cruise boat in luzzu colours: an awning on posts, a festoon of bulbs under it, people along the rail
const CRUISE_ROWS = [
  '.........kkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkkk.........',
  '.........kLyLyLyLyLyLyLyLyLyLyLyLyLyLyLyLyLyk.........',
  '.........k..h..h...h..h..h...h..h...h..h...k.........',
  '.........k.ccc.ccc.ccc.ccc.ccc.ccc.ccc.ccc.k.........',
  'Y........kcccccccccccccccccccccccccccccccc.k.......y.',
  'YYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYYy.',
  '.RRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRr..',
  '.BBekBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBb..',
  '..BBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBBb...',
  '..GGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGg...',
  '...wwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwwww....',
];
// a dgħajsa, the old harbour water-taxi: slender, high stem and sternpost, rowed standing
const DGH_ROWS = ['Y................y', '.YRRRRRRRRRRRRRRy.', '..BBBBBBBBBBBBBB..', '...GGGGGGGGGGGG...'];

/* ================= The Strand: kerb, railing, paving, the lamp, a bench ================= */
const FERRY_SIGN = { x: 190, y: 168 };              // the ferry stop's sign beside the pontoon's steps
const GAPS = [[132, 186], [304, 346]];                // the railing opens on the ferry pontoon and on the luzzu's steps
const LUZZI = [{ x: 44, y: 187 }, { x: 310, y: 187 }];   // moored luzzus, top-left of their sprites (the second goes out)
function renderStrand() {
  const p = painter(W, H), put = p.put;
  // the kerb's limestone edge, then the paving: big worn limestone slabs, warm under the lamp and the shop
  const slab = (x, y) => { const row = Math.floor((y - KERB - 3) / 10), off = (row * 13) % 19; return [row, Math.floor((x + off) / (16 + (row % 3) * 3))]; };
  for (let x = 0; x < W; x++) for (let y = KERB; y < H; y++) {
    if (y < KERB + 3) { put(x, y, y === KERB ? '#d0b080' : y === KERB + 1 ? '#8a6e52' : '#3e3234'); continue; }
    const warm = Math.max(0, 1 - Math.hypot((x - LAMP.x) / 120, (y - 228) / 50)) * 0.5 + Math.max(0, 1 - Math.hypot((x - 410) / 110, (y - 214) / 42)) * 0.55;
    const [row, col] = slab(x, y), [, colL] = slab(x - 1, y), [rowU] = slab(x, y - 1);
    const joint = row !== rowU || col !== colL;
    let v = 0.4 + warm * 0.6 - (y - KERB) / 70 * 0.16 + (hash(row, col, 3) - 0.5) * 0.1 + (bayer(x, y) - 0.5) * 0.08;
    const PAVE = ['#201c24', '#2a242a', '#382e30', '#4a3c36', '#604c3e', '#7a6048'];
    // the joints are a solid step darker than the slab around them, so they read as stone edges, not dotted lines
    put(x, y, joint ? PAVE[Math.max(0, Math.round(clamp(v, 0, 1) * 5) - 1)] : ramp(PAVE, v, x, y));
  }
  // the railing: painted posts and two rails, opening at the steps
  for (let x = 0; x < HOUSE.x0 - 6; x++) {
    if (GAPS.some(([a, b]) => x >= a && x <= b)) continue;
    const post = x % 14 === 0, lit = Math.max(0, 1 - Math.abs(x - LAMP.x) / 90);
    const hi = mixHex('#3a4456', '#c8a878', lit * 0.6), lo = '#1a1e2a';
    put(x, 189, hi); put(x, 190, lo); put(x, 195, hi);
    if (post) for (let y = 189; y < KERB; y++) { put(x, y, y === 189 ? mixHex(hi, '#ffffff', 0.2) : hi); put(x + 1, y, lo); }
  }
  // stone bollards at the steps
  for (const [a, b] of GAPS) for (const bx of [a + 2, b - 4]) for (let y = 191; y < KERB; y++) for (let i = bx; i < bx + 3; i++) put(i, y, y === 191 ? '#d8b888' : i === bx ? '#b09070' : '#6a5444');
  // the lamp: a tall dark post with a lantern
  const { x: lx, y: ly } = LAMP;
  for (let y = ly + 6; y < 228; y++) { put(lx, y, '#2a2e3a'); put(lx + 1, y, '#14161e'); }
  for (let i = lx - 2; i <= lx + 3; i++) { put(i, 226, '#2a2e3a'); put(i, 227, '#1a1c26'); }
  for (let i = lx - 1; i <= lx + 2; i++) put(i, 216, '#3a3e4c');
  for (let y = ly - 4; y <= ly + 5; y++) for (let i = lx - 3; i <= lx + 4; i++) {
    const frame = i === lx - 3 || i === lx + 4 || y === ly - 4 || y === ly + 5 || y === ly - 3;
    put(i, y, frame ? '#22242e' : y < ly ? '#fff2c8' : '#ffd890');
  }
  for (let i = lx - 2; i <= lx + 3; i++) put(i, ly - 5, '#22242e');
  put(lx, ly - 6, '#22242e'); put(lx + 1, ly - 6, '#22242e');
  // the ferry stop's sign at the steps: FERRY over VALLETTA, on a post
  for (let y = FERRY_SIGN.y + 9; y < 204; y++) { put(FERRY_SIGN.x + 18, y, '#3a3e4c'); put(FERRY_SIGN.x + 19, y, '#1e2028'); }
  for (let y = FERRY_SIGN.y; y < FERRY_SIGN.y + 14; y++) for (let i = FERRY_SIGN.x; i < FERRY_SIGN.x + 37; i++)
    put(i, y, i === FERRY_SIGN.x || i === FERRY_SIGN.x + 36 || y === FERRY_SIGN.y || y === FERRY_SIGN.y + 13 ? '#141e34' : '#1e3860');
  // a stone planter of oleander by the lamp
  for (let y = 228; y < 242; y++) for (let i = 62; i < 96; i++) put(i, y, y === 228 ? '#d8b888' : y === 229 ? '#a88a64' : i === 62 ? '#b09070' : i === 95 ? '#4a3a32' : ramp(['#4a3a32', '#6a5444', '#8a6e54'], 0.7 - (y - 228) / 20 + (bayer(i, y) - 0.5) * 0.1, i, y));
  { const r = rng(83);
    for (let k = 0; k < 150; k++) {
      const x = Math.round(64 + r() * 30), y = Math.round(226 - Math.pow(r(), 0.7) * 16 + Math.abs(x - 79) * 0.3);
      const bloom = r() < 0.32;
      put(x, y, bloom ? (r() < 0.5 ? '#e87aa0' : '#f4a0bc') : r() < 0.5 ? '#1e3a24' : '#2e4e2e');
    }
  }
  // the pastizzeria's two tables on the paving, bentwood chairs
  for (const tx of [346, 372]) {
    for (let i = tx - 5; i <= tx + 5; i++) { put(i, 224, '#e8e0d0'); put(i, 225, '#a8a094'); }
    for (let y = 226; y < 235; y++) put(tx, y, '#3a3a42');
    for (let i = tx - 3; i <= tx + 3; i++) put(i, 235, '#3a3a42');
  }
  // a cream parasol over each table, its underside warm from the bulb hung under it
  for (const tx of [346, 372]) {
    for (let y = 203; y < 224; y++) put(tx, y, '#4a443e');
    for (let k = 0; k < 5; k++) for (let i = tx - 4 - k * 2; i <= tx + 4 + k * 2; i++) put(i, 199 + k, k === 0 ? '#f4ead2' : k < 3 ? '#e2d4b4' : '#c8b490');
    for (let i = tx - 12; i <= tx + 12; i++) put(i, 204, (i - tx) % 3 === 0 ? '#a8946e' : '#f0d8a0');
    put(tx, 205, '#fff0c0');
  }
  // a limestone bench facing the water, near the viewer
  for (let i = 196; i < 236; i++) { put(i, 236, '#c8a880'); put(i, 237, '#9a7c5e'); put(i, 238, '#5a4638'); }
  for (const bx of [199, 230]) for (let y = 239; y < 245; y++) { put(bx, y, '#8a6e54'); put(bx + 1, y, '#5a4638'); put(bx + 2, y, '#3a2e2a'); }
  const c = p.done();
  sign(p.x, 'FERRY', FERRY_SIGN.x + Math.round((37 - signWidth('FERRY')) / 2), FERRY_SIGN.y + 2, '#f2ece0');
  sign(p.x, 'VALLETTA', FERRY_SIGN.x + Math.round((37 - signWidth('VALLETTA')) / 2), FERRY_SIGN.y + 8, '#ffd24a');
  return c;
}

/* ================= The Sliema townhouse: the cozy corner ================= */
// honey limestone, three storeys: a green gallarija on stone corbels on the first floor, lit from inside; a
// pastizzeria on the ground floor, its arched door open on the counter; the house's own green door; bougainvillea
const HL_STONE = ['#f4d8a0', '#dcbc80', '#bc9a62', '#8e7048', '#5e4a34'];
const SHOP = { x0: 398, x1: 418, top: 180 };
function renderHouse() {
  const p = painter(W, H), put = p.put, { x0, roof, floor } = HOUSE;
  for (let y = roof - 2; y < floor; y++) for (let x = x0; x < W; x++) {
    const glow = Math.max(0, 1 - Math.hypot((x - 410) / 70, (y - 196) / 50)) * 0.45;
    const v = 0.25 + (y - roof) / (floor - roof) * 0.25 + glow + (bayer(x, y) - 0.5) * 0.06;
    let col = ramp([...HL_STONE].reverse(), v, x, y);
    if (x < x0 + 3) col = x === x0 ? HL_STONE[1] : ((y - roof) / 6 | 0) % 2 ? HL_STONE[2] : HL_STONE[1];   // the quoins
    if (y === roof - 2) col = HL_STONE[0];
    if (y === roof - 1 || y === roof + 2) col = HL_STONE[3];
    if (y === roof || y === roof + 1) col = HL_STONE[1];                                                 // the cornice
    put(x, y, col);
  }
  // second floor: two tall shuttered windows, one open and lit
  for (const [wx, open] of [[402, false], [456, true]]) for (let y = 128; y < 144; y++) for (let i = wx; i < wx + 9; i++) {
    const frameC = i === wx || i === wx + 8 || y === 128;
    let col = frameC ? HL_STONE[0] : open ? (i < wx + 2 || i > wx + 6 ? '#3a6a46' : y < 136 ? '#ffd890' : '#f0b060') : ((i - wx) % 2 ? '#2e5a3a' : '#3a6e48');
    if (open && i === wx + 4 && y > 129) col = '#5a3a28';
    put(i, y, col);
  }
  for (const wx of [402, 456]) for (let i = wx - 1; i < wx + 10; i++) put(i, 144, HL_STONE[0]);
  // the gallarija: cornice, a row of lit panes in green frames, panelled base, on four stone corbels
  const G0 = 398, G1 = 446, GT = 149, GB = 170;
  for (let y = GT; y <= GB; y++) for (let x = G0; x <= G1; x++) {
    let col;
    if (y === GT) col = '#5ab070'; else if (y === GT + 1) col = '#2a6a3e';
    else if (y < GT + 12) {
      const pane = (x - G0) % 6, pr = (y - GT - 2) % 5;
      col = pane === 0 || pr === 4 ? '#2e7a46' : (y - GT) < 6 ? '#ffe6a8' : '#f8c870';
      if (pane !== 0 && pr !== 4 && hash(x >> 2, y, 5) < 0.12) col = '#e8a858';
      if (x > 420 && x < 432 && y > GT + 4 && pane !== 0 && pr !== 4) col = '#c87a48';   // a curtain half-drawn
    } else if (y === GT + 12) col = '#5ab070';
    else col = (x - G0) % 8 === 0 || y === GB ? '#1e5232' : (x - G0) % 8 < 4 ? '#2e7a46' : '#286a3c';
    if (x === G0 || x === G1) col = '#1e5232';
    put(x, y, col);
  }
  for (const cx of [401, 416, 430, 443]) for (let y = GB + 1; y <= GB + 3; y++) for (let i = cx - (GB + 3 - y); i <= cx + 1; i++) put(i, y, y === GB + 1 ? HL_STONE[0] : HL_STONE[2]);
  // the pastizzeria: its board, the arched door open on the counter, the window with trays of pastizzi
  for (let y = 174; y < 180; y++) for (let x = 396; x < 452; x++) put(x, y, y === 174 || y === 179 ? '#1a3a26' : '#24502f');
  for (let y = SHOP.top; y < floor; y++) for (let x = SHOP.x0; x <= SHOP.x1; x++) {
    const arch = y < SHOP.top + 3 && Math.abs(x - (SHOP.x0 + SHOP.x1) / 2) > 10 - (y - SHOP.top) * 2.5;
    if (arch) continue;
    put(x, y, x === SHOP.x0 || x === SHOP.x1 ? '#e8c890' : y < 194 ? '#f8d090' : '#e8b068');
  }
  for (let x = SHOP.x0 + 3; x <= SHOP.x1 - 3; x++) for (let y = 194; y < floor - 1; y++) put(x, y, y === 194 ? '#fff4d8' : y < 198 ? ((x + y) % 4 === 0 ? '#e8a848' : '#f8e8c8') : '#8a5a38');
  for (let y = 182; y < 200; y++) for (let x = 424; x < 450; x++) {
    let col = x === 424 || x === 449 || y === 182 || y === 199 ? '#1e4a2c' : y < 190 ? '#fae0a8' : '#f2c880';
    if (y >= 192 && y <= 197 && x > 425 && x < 448 && (x + (y & 1) * 2) % 4 === 0) col = '#d89a48';   // pastizzi on their trays
    if (y === 191 || y === 198) col = '#c8b090';
    put(x, y, col);
  }
  // the house's own door, painted green, with a fanlight and a brass knocker
  for (let y = 178; y < floor; y++) for (let x = 458; x < 472; x++) {
    let col = x === 458 || x === 471 ? HL_STONE[0] : y < 183 ? ((x + y) % 2 ? '#e8b868' : '#a87838') : (x - 458) % 6 === 0 ? '#1e4a2c' : '#2e6a40';
    if (x === 464 && y === 192) col = '#f0c860';
    put(x, y, col);
  }
  // bougainvillea over the house's corner: a dark mass of leaves cascading from the first floor, magenta bracts on it
  { const r = rng(71), blobs = [];
    for (let k = 0; k < 9; k++) blobs.push({ x: x0 - 2 + r() * 9, y: 148 + k * 4 + r() * 3, rx: 3 + r() * 2.5, ry: 2.5 + r() * 1.5 });
    for (let y = 140; y < 190; y++) for (let x = x0 - 8; x < x0 + 10; x++) {
      let v = 0;
      for (const b of blobs) v = Math.max(v, 1 - ((x - b.x) / b.rx) ** 2 - ((y - b.y) / b.ry) ** 2);
      if (v <= 0.05) continue;
      const h = hash(x, y, 71);
      put(x, y, h < 0.42 ? (h < 0.14 ? '#f070b0' : h < 0.3 ? '#d03c8c' : '#a82a72') : v > 0.5 ? '#1e3422' : '#2a4a2c');
    }
  }
  const c = p.done();
  sign(p.x, 'PASTIZZI', 396 + Math.round((56 - signWidth('PASTIZZI')) / 2), 175, '#f4e4b8');
  return c;
}

/* ================= The roof sign: the billboard ================= */
// a steel frame on the parapet with two posts and three lamps; the city's own ad is a harbour boat-trip outfit,
// IL-LUZZU, painted like a luzzu's hull (blue, a yellow and a red band), a luzzu's eye each side of the name
const BB_LAMPS = [0.2, 0.5, 0.8].map(f => BB.x + Math.round((BB.w - 1) * f));
function renderBillboard() {
  const p = painter(W, H), put = p.put, X0 = BB.x - 2, X1 = BB.x + BB.w + 1, Y0 = BB.y - 2, Y1 = BB.y + BB.h + 1;
  for (const cx of [BB.x + 6, BB.x + BB.w - 7]) for (let y = Y1 + 1; y < HOUSE.roof - 2; y++) { put(cx, y, '#3a3a48'); put(cx + 1, y, '#16161e'); }
  for (let x = X0; x <= X1; x++) { put(x, Y0, '#14141c'); put(x, Y1, '#14141c'); put(x, Y0 + 1, '#2e3040'); put(x, Y1 - 1, '#2a2a36'); }
  for (let y = Y0; y <= Y1; y++) { put(X0, y, '#14141c'); put(X1, y, '#14141c'); put(X0 + 1, y, '#2a2c3c'); put(X1 - 1, y, '#16161e'); }
  for (const lx of BB_LAMPS) {
    for (let y = Y0 - 4; y < Y0; y++) put(lx, y, '#3a3a48');
    for (let i = lx - 2; i <= lx + 2; i++) put(i, Y0 - 5, i === lx - 2 ? '#4a4a5a' : '#2a2a36');
  }
  return p.done();
}
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const lamp = Math.min(...BB_LAMPS.map(lx => Math.abs(BB.x + x - lx))) / 9;
    let col = ramp(['#3a7ac8', '#2e66b0', '#24528e', '#1a3e6e'], clamp(y / h * 0.7 + lamp * 0.25, 0, 1), x, y);
    if (y === h - 4) col = '#f0c430'; else if (y === h - 3) col = '#e0b028'; else if (y >= h - 2) col = '#c83a2e';
    put(x, y, col);
  }
  const eye = (ex, flip) => {
    const E = ['.kkkk..', 'kwwkkw.', '.wwkkww', '..kkk..'];
    E.forEach((row, j) => { for (let i = 0; i < 7; i++) { const ch = row[flip ? 6 - i : i]; if (ch !== '.') put(ex + i, 3 + j, ch === 'k' ? '#14141c' : '#f4f0e4'); } });
  };
  eye(1, false); eye(w - 8, true);
  const c = p.done();
  sign(p.x, 'IL-LUZZU', Math.round((w - signWidth('IL-LUZZU')) / 2), 3, '#ffd24a');
  sign(p.x, 'BOAT TRIPS', Math.round((w - signWidth('BOAT TRIPS')) / 2), 10, '#f4ecd8');
  return gradeAd(c, { level: 0.86, tint: NIGHT, mix: 0.08, wear: 0.05, seed: 29 });
}

/* ================= People ================= */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const SKINS = ['#f2c8a0', '#e0aa80', '#c08a60', '#8a5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#c8a058', '#8e3e1c', '#c8c4c0'];
const PNIGHT = '#141a3a', GLOW = '#ffd8a0';
// a look for a person h px tall: proportions and colours dimmed for the night, with a warm line of light
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.22, m = c => mixHex(c, PNIGHT, dk);
  const hh = h >= 20 ? 5 : 4, th = Math.round(h * 0.37);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 21 ? 7 : h >= 18 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOW, 0.3), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.28),
    topL: mixHex(top, GLOW, 0.32), bot: m(bot), shoe: m('#221a20'), long: o.long || 0, skirt: o.skirt ? m(o.skirt) : null,
    hat: o.hat ? m(o.hat) : null, apron: o.apron ? m(o.apron) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * o.aL / o.aR: 0 down, 2 phone up, 3 out (holding a rod, offering), 5 hand to the face (eating, a glass)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1), legC = P.skirt ? P.skin : P.bot;
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, legC);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, legC); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, legC); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, legC); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
      if (P.skirt) R(x - 2, ly, 4, (legs >> 1) + 1, P.skirt);
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, legC); R(tx + bw - lw, ly + 2, lw, legs - 2, legC);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
      if (P.skirt) R(tx, ly, bw, (legs >> 1) + 1, P.skirt);
    }
  } else if (o.sit && view === 1) {
    R(tx, fy + 1, 2, 4, legC); R(tx + bw - 2, fy + 1, 2, 4, legC); R(tx, fy + 5, 2, 1, P.shoe); R(tx + bw - 2, fy + 5, 2, 1, P.shoe);
  } else if (o.sit && side) {
    const tl = P.h >= 22 ? 7 : 5, sh = o.shin ?? 4;
    R(dir > 0 ? x - 1 : x - tl + 2, fy - 1, tl, 2, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 2, fy + 1, 2, sh, P.bot); R(dir > 0 ? x + tl - 3 : x - tl + 1, fy + 1 + sh, 3, 1, P.shoe);
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);                                                  // the lamps' light on the shoulders
  if (P.apron && view !== 0) R(side ? (dir > 0 ? x : x - 1) : tx + 1, ty + 2, side ? 2 : bw - 2, th + 2, P.apron);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax, hy + 1, 1, 1, P.skin); const p0 = sg < 0 ? ax - 1 : ax; R(p0, hy - 2, 2, 3, '#0e1018'); if (view === 0) { R(p0, hy - 2, 2, 1, '#ffe2a8'); R(p0, hy - 1, 2, 1, '#8ad8e8'); } }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 5) { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, hy + hh - 1, 1, 2, P.skin); }
    else { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx - 1, hy, hw + 2, 1, P.hat); }
  return hy;
}
const TOPS = ['#c8404a', '#e0b840', '#e8e2d4', '#3a64c0', '#2a9a8a', '#d87aa0', '#e0803a', '#4a8a4a', '#7a5ac0', '#c0c8d8', '#8a2a34', '#5aa8d0'];
const BOTS = ['#33466e', '#22202c', '#a8926a', '#d0ccc4', '#3a3a58', '#4a3a30'];

/* ================= The loop's timeline (seconds) =================
   0-6      the ferry waits at Valletta's Marsamxett landing, lit; cars along the quay road; the pastizzeria's queue
   6-42     the ferry crosses towards us, growing; two passengers walk up to the pontoon (14-23); horn at 38; it docks at 42
   25, 65, 105, 145, 185, 225   the fisherman reels in a fish, unhooks it and casts again with a splash (every 40 s)
   26-40    gulls cross            44-84   a harbour-cruise boat in luzzu colours passes, left to right, festoon lit
   45-62    four passengers come off the ferry and walk away; 64-67 the two waiting board
   60-63    the Carmelite bell strikes three; pigeons wheel round the dome till 84
   72       horn; the ferry backs out and crosses back to Valletta by 108
   84-106, 210-232   a plane on its way down to the airport crosses, descending
   112-126  the luzzu's skipper walks up and steps aboard; 128 its engine; it backs out and turns for Msida, behind the house
   112-126  gulls              150-152 the Pro-Cathedral's bells              150-180 a dgħajsa is rowed across
   174-204  festa fireworks over Valletta: a rocket's trail, nine bursts; the railing films and points; smoke drifts off
   200-222  the luzzu comes home and moors; 223-231 its skipper steps off and walks away
   222-236  gulls              every 12 s  a customer is served at the pastizzeria and walks off eating; the queue shuffles up
*/
const FERRY = { a: 6, dock: 42, off: 72, back: 108 };
const FERRY_V = { x: 300, y: 166 }, FERRY_S = { x: 156, y: 197 };   // bottom-centre at Valletta's landing and at the pontoon
const BELLS_C = [60, 61.6, 63.2], BELLS_P = [150, 151.8];
const FLOCK = { t0: 60, t1: 84 };
const PLANES = [{ t0: 84, t1: 106, dir: -1 }, { t0: 210, t1: 232, dir: 1 }];
const CRUISE = { t0: 44, t1: 84, y: 183 };
const DGH = { t0: 150, t1: 180, y: 181 };
const LUZ = { out: 128, turn: 134, gone: 150, home: 200, moor: 218, still: 222 };
const MOOR = { x: LUZZI[1].x + 16, y: LUZZI[1].y + 9 };            // the second luzzu's bottom-centre at its mooring
const GULLS = [[26, 40, 1, 64], [112, 126, -1, 52], [222, 236, 1, 72]];
const CATCHES = [25, 65, 105, 145, 185, 225];
const FW = { t0: 174, t1: 210 };
const BURSTS = [
  { t: 176, x: 132, y: 60, c: 0 }, { t: 179.5, x: 310, y: 64, c: 1 }, { t: 183, x: 146, y: 52, c: 2 },
  { t: 186.5, x: 108, y: 68, c: 3 }, { t: 189.5, x: 318, y: 58, c: 0 }, { t: 192.5, x: 128, y: 72, c: 1 },
  { t: 195.5, x: 302, y: 72, c: 2 }, { t: 198.5, x: 118, y: 56, c: 0 }, { t: 199.4, x: 150, y: 66, c: 3 },
];
const FW_COLS = [['#fff4c0', '#ffc860', '#e8902c'], ['#ffe0d8', '#ff6a50', '#c83a2e'], ['#e4ffd8', '#68e080', '#2e9a4e'], ['#ffffff', '#d8e4ff', '#8ea8e8']];
const BURST_LIFE = 3.6, ROCKET = 1.2;
const ease = (a, b, t) => smooth(a, b, t);

/* ================= The ferry ================= */
function ferryAt(t) {
  if (t >= FERRY.a && t < FERRY.dock) return { u: ease(FERRY.a, FERRY.dock, t), dir: -1, moving: true };
  if (t >= FERRY.dock && t < FERRY.off) return { u: 1, dir: -1, moving: false };
  if (t >= FERRY.off && t < FERRY.back) return { u: 1 - ease(FERRY.off, FERRY.back, t), dir: 1, moving: true };
  return { u: 0, dir: 1, moving: false };
}
const ferryPos = u => ({ x: lerp(FERRY_V.x, FERRY_S.x, u), y: lerp(FERRY_V.y, FERRY_S.y, Math.pow(u, 1.35)) });
function drawFerry(t, f) {
  const { x, y } = ferryPos(f.u), bob = f.u === 1 ? Math.round(0.6 * wave(t, 60)) : 0;
  const S = f.u < 0.3 ? SPR.ferryF : f.u < 0.66 ? (f.dir > 0 ? SPR.ferryM : SPR.ferryMR) : (f.dir > 0 ? SPR.ferryN : SPR.ferryNR);
  const x0 = Math.round(x - S.width / 2), y0 = Math.round(y) - S.height + 1 + bob;
  halo(f.u < 0.3 ? HL.ferryFar : HL.ferry, x, y - S.height / 2, 0.8);
  ctx.globalAlpha = 0.28; ctx.drawImage(S, 0, 2, S.width, S.height - 3, x0, y0 + S.height + 1, S.width, Math.max(2, (S.height - 3) >> 1)); ctx.globalAlpha = 1;
  ctx.drawImage(S, x0, y0);
  if (f.moving) {                                             // its wake, trailing from the stern
    const back = -f.dir * (f.u < 0.66 ? 1 : -1);
    ctx.fillStyle = '#c8d4e8';
    for (let k = 1; k <= 7; k++) {
      ctx.globalAlpha = 0.5 * (1 - k / 8) * (0.6 + 0.4 * hash(k, step(t, 960)));
      ctx.fillRect(Math.round(x + back * (S.width / 2 + k * 2)), Math.round(y) - (k & 1), 2, 1);
    }
    ctx.globalAlpha = 1;
  }
  return { x, y, w: S.width };
}

/* ================= The second luzzu's trip: out to Msida behind the house, and home ================= */
function luzzuAt(t) {
  if (t >= LUZ.out && t < LUZ.turn) { const u = ease(LUZ.out, LUZ.turn, t); return { x: MOOR.x - 6 * u, y: MOOR.y - 5 * u, face: -1, crew: true }; }
  if (t >= LUZ.turn && t < LUZ.gone) { const u = ease(LUZ.turn, LUZ.gone, t); return { x: lerp(MOOR.x - 6, 412, u), y: lerp(MOOR.y - 5, 183, u), face: 1, crew: true }; }
  if (t >= LUZ.gone && t < LUZ.home) return null;
  if (t >= LUZ.home && t < LUZ.moor) { const u = ease(LUZ.home, LUZ.moor, t); return { x: lerp(412, MOOR.x - 4, u), y: lerp(183, MOOR.y - 4, u), face: -1, crew: true }; }
  if (t >= LUZ.moor && t < LUZ.still) { const u = ease(LUZ.moor, LUZ.still, t); return { x: MOOR.x - 4 + 4 * u, y: MOOR.y - 4 + 4 * u, face: -1, crew: t < 223 }; }
  return { x: MOOR.x, y: MOOR.y, face: -1, crew: false, moored: true };
}
function drawLuzzu(t, L, i) {
  const S = L.y < 186 ? (L.face > 0 ? SPR.luzMR : SPR.luzM) : (L.face > 0 ? SPR.luzNR : SPR.luzN);
  const wake = L.moored ? 0 : 1, bob = Math.round((0.5 + 0.6 * wake) * wave(t, 48 + i * 8, i * 0.3) + 1.2 * ferryWake(t, L.x) * wave(t, 160, i * 0.2));
  const x0 = Math.round(L.x - S.width / 2), y0 = Math.round(L.y) - S.height + 1 + bob;
  ctx.globalAlpha = 0.32; ctx.drawImage(S, 0, 3, S.width, S.height - 4, x0, y0 + S.height + 1, S.width, (S.height - 4) >> 1); ctx.globalAlpha = 1;
  ctx.drawImage(S, x0, y0);
  if (L.crew) {                                               // the skipper at the tiller, a lantern on the stern
    const sx = L.face > 0 ? x0 + 4 : x0 + S.width - 6;
    R(sx, y0 - 4, 2, 4, '#3a5a8a'); R(sx, y0 - 6, 2, 2, mixHex(SKINS[2], PNIGHT, 0.2)); R(sx, y0 - 7, 2, 1, '#2a2a34');
    const lx = L.face > 0 ? x0 + 1 : x0 + S.width - 2;
    R(lx, y0 - 3, 1, 2, '#ffe2a0'); halo(HL.lantern, lx, y0 - 2, 0.8);
    if (!L.moored) { ctx.fillStyle = '#c8d4e8'; for (let k = 1; k <= 5; k++) { ctx.globalAlpha = 0.45 * (1 - k / 6); ctx.fillRect(Math.round(L.x - L.face * (S.width / 2 + k * 2)), Math.round(L.y), 2, 1); } ctx.globalAlpha = 1; }
  }
}
// how much the ferry's wake rocks a boat at x (0..1)
function ferryWake(t, x) {
  const f = ferryAt(t);
  if (!f.moving || f.u < 0.6) return 0;
  return clamp(1 - Math.abs(ferryPos(f.u).x - x) / 90, 0, 1);
}

/* ================= The cruise boat and the dgħajsa ================= */
function drawCruise(t) {
  if (t < CRUISE.t0 || t >= CRUISE.t1) return;
  const S = SPR.cruiseR, x = Math.round(lerp(-S.width - 6, W + 6, (t - CRUISE.t0) / (CRUISE.t1 - CRUISE.t0))), y0 = CRUISE.y - S.height + 1;
  ctx.globalAlpha = 0.3; ctx.drawImage(S, 0, 5, S.width, 6, x, CRUISE.y + 2, S.width, 3); ctx.globalAlpha = 1;
  halo(HL.cruise, x + S.width / 2, y0 + 3, 0.9);
  ctx.drawImage(S, x, y0);
  for (let k = 0; k < 17; k++) {                              // the festoon's bulbs twinkle
    if (hash(k, step(t, 480), 31) < 0.25) continue;
    R(x + S.width - 12 - (11 + k * 2), y0 + 1, 1, 1, k % 3 ? '#ffe8a0' : '#ffb0a0');
  }
  streak(ctx, t, x + S.width / 2, 14, '#ffd890', 0.25, 13, CRUISE.y + 2, KERB);
  ctx.fillStyle = '#c8d4e8';
  for (let k = 1; k <= 8; k++) { ctx.globalAlpha = 0.5 * (1 - k / 9); ctx.fillRect(x - k * 3, CRUISE.y - (k & 1), 3, 1); }
  ctx.globalAlpha = 1;
}
function drawDghajsa(t) {
  if (t < DGH.t0 || t >= DGH.t1) return;
  const S = SPR.dgh, x = Math.round(lerp(W + 4, -S.width - 4, (t - DGH.t0) / (DGH.t1 - DGH.t0))), y0 = DGH.y - S.height + 1;
  const stroke = frac(t / 2), oar = stroke < 0.5 ? 1 : -1;     // a stroke every 2 s
  ctx.globalAlpha = 0.3; ctx.drawImage(S, 0, 1, S.width, 3, x, DGH.y + 2, S.width, 2); ctx.globalAlpha = 1;
  ctx.drawImage(S, x, y0);
  const rx = x + 9;                                            // the boatman stands amidships, rowing forward
  R(rx, y0 - 5, 2, 5, '#d8d4c8'); R(rx, y0 - 7, 2, 2, mixHex(SKINS[1], PNIGHT, 0.3)); R(rx, y0 - 8, 2, 1, '#2a2226');
  ctx.fillStyle = '#8a6a48';
  for (let k = 0; k < 6; k++) ctx.fillRect(rx - 1 - k + (oar > 0 ? 0 : 1), y0 - 3 + k, 1, 1);
}

/* ================= The sky's life: a plane, gulls, pigeons round the dome ================= */
function drawPlane(t) {
  const pl = PLANES.find(p => t >= p.t0 && t < p.t1);
  if (!pl) return;
  const u = (t - pl.t0) / (pl.t1 - pl.t0), x = Math.round(pl.dir < 0 ? lerp(W + 10, -10, u) : lerp(-10, W + 10, u)), y = Math.round(lerp(24, 40, u));
  R(x, y, 4, 1, '#5a5e70'); R(x + 2, y - 1, 1, 1, '#4a4e60');
  R(x, y, 1, 1, '#f4f0e0');                                     // its landing light
  if (frac(t) < 0.18) R(x + 2, y + 1, 1, 1, '#ff4a3a');          // the beacon blinks once a second
  halo(HL.planeL, x, y, 0.6);
}
function gull(x, y, flap, col = '#e8eaf0') {
  x = Math.round(x); y = Math.round(y);
  R(x, y, 1, 1, col); R(x + 4, y, 1, 1, col);
  if (flap) { R(x + 1, y - 1, 1, 1, col); R(x + 3, y - 1, 1, 1, col); } else { R(x + 1, y + 1, 1, 1, col); R(x + 3, y + 1, 1, 1, col); }
  R(x + 2, y, 1, 1, '#b8bcc8');
}
function drawGulls(t) {
  for (const [a, b, dir, y] of GULLS) {
    if (t < a || t >= b) continue;
    const u = (t - a) / (b - a);
    for (let k = 0; k < 3; k++) {
      const x = dir > 0 ? lerp(-20 - k * 14, W + 10 - k * 14, u) : lerp(W + 10 + k * 14, -20 + k * 14, u);
      gull(x, y + k * 5 + 2 * Math.sin(u * 9 + k), step(t + k * 0.13, 1200) % 2);
    }
  }
}
// at the first bell the campanile's pigeons go up and wheel round the dome, then settle back
function drawFlock(t) {
  if (t < FLOCK.t0 || t >= FLOCK.t1) return;
  const u = (t - FLOCK.t0) / (FLOCK.t1 - FLOCK.t0), lift = Math.min(1, u * 6, (1 - u) * 6);
  for (let k = 0; k < 6; k++) {
    const a = TAU * (u * 2.2 + k / 6), x = lerp(CAMP.cx, DOME.cx + Math.cos(a) * 44, lift), y = lerp(CAMP.top + 2, 58 + Math.sin(a) * 12, lift);
    const flap = step(t + k * 0.11, 1440) % 2, c = '#c8c4cc';
    R(Math.round(x), Math.round(y), 2, 1, '#8a8690'); R(Math.round(x) + (flap ? -1 : 2), Math.round(y) - flap, 1, 1, c);
  }
}

/* ================= Festa fireworks over Valletta: bursts in the sky, never the frame ================= */
// each burst: a rocket's trail from the roofs, then 22 sparks thrown out, drooping and fading, twinkling at the end;
// its own small glow, a coloured glint on the water, a puff of smoke that drifts off
const SPARKS = Array.from({ length: 34 }, (_, i) => ({ a: TAU * (i + hash(i, 61) * 0.5) / 34, r: 0.72 + hash(i, 62) * 0.36 }));
function burstState(t, b) {
  const r0 = b.t - ROCKET;
  if (t < r0 || t >= b.t + BURST_LIFE + 3) return null;
  return { rising: t < b.t, u: t < b.t ? (t - r0) / ROCKET : (t - b.t) / BURST_LIFE };
}
function drawFireworks(t, reducedMotion) {
  if (t < FW.t0 || t >= FW.t1) return;
  BURSTS.forEach((b, bi) => {
    const st = burstState(t, b);
    if (!st) return;
    const C = FW_COLS[b.c];
    if (st.rising) {                                             // the rocket climbing, a short fading trail
      const y = lerp(118, b.y, Math.sqrt(st.u)), x = b.x + Math.round((1 - st.u) * 3);
      for (let k = 0; k < 6; k++) { ctx.globalAlpha = 0.8 * (1 - k / 6); R(x, Math.round(y) + k, 1, 1, k ? '#e8b878' : '#fff4d8'); }
      ctx.globalAlpha = 1;
      return;
    }
    const u = st.u;
    if (u < 1) {
      if (u < 0.22 && !reducedMotion) halo(HL.fw[b.c], b.x, b.y, 0.75 * (1 - u / 0.22));
      const R0 = (b.x > 240 ? 17 : 20) + (bi % 3) * 2, droop = 9 * u * u, fade = 1 - Math.pow(u, 1.8), fr = Math.floor(t * 30);
      SPARKS.forEach((s, i) => {
        if (u > 0.5 && hash(i, fr, bi) < 0.45 * u) return;      // twinkling out
        const rr = R0 * s.r * (1 - Math.pow(1 - u, 3)), cx = Math.cos(s.a), sy = Math.sin(s.a);
        for (let j = 3; j >= 0; j--) {                           // the head and its trail back towards the centre
          const k = 1 - j * 0.13 * (1 - u * 0.4);
          ctx.globalAlpha = fade * (j ? 0.55 - j * 0.13 : 1);
          R(Math.round(b.x + cx * rr * k), Math.round(b.y + sy * rr * k + droop * k * k), 1, 1, j ? FW_COLS[b.c][2] : u < 0.3 ? FW_COLS[b.c][0] : FW_COLS[b.c][1]);
        }
      });
      ctx.globalAlpha = 1;
    }
    const smoke = Math.min(1, u * 2) * (1 - smooth(0.7, 1 + 3 / BURST_LIFE, u)) * 0.3;   // smoke drifting off to the right
    if (smoke > 0.01) {
      ctx.globalAlpha = smoke; ctx.fillStyle = '#5a5a78';
      for (let k = 0; k < 18; k++) ctx.fillRect(Math.round(b.x - 10 + hash(k, bi, 71) * 20 + u * 7), Math.round(b.y + 2 + hash(k, bi, 72) * 10 + u * 3), 2, 1);
      ctx.globalAlpha = 1;
    }
  });
}
function drawFireworkGlints(t) {
  if (t < FW.t0 || t >= FW.t1) return;
  for (const b of BURSTS) {
    const st = burstState(t, b);
    if (!st || st.rising || st.u >= 1) continue;
    streak(ctx, t, b.x, 6, FW_COLS[b.c][1], 0.5 * (1 - st.u), b.x * 0.3, WL + 2, KERB - 2);
  }
}
// how lit the sky is by the fireworks just now (0..1), for the faces that look up
const fwOn = t => t >= FW.t0 && t < FW.t1 - 4;

/* ================= Valletta's own small life: cars on the quay road, windows, bells ================= */
const CARS = [{ n: 3, ph: 0.1, dir: 1 }, { n: 2, ph: 0.55, dir: -1 }, { n: 4, ph: 0.32, dir: 1 }, { n: 3, ph: 0.8, dir: -1 }, { n: 2, ph: 0.2, dir: -1 }];
function drawCars(t) {
  for (const c of CARS) {
    const u = frac(c.n * t / LOOP + c.ph);
    if (u > 0.22) continue;
    const v = u / 0.22, x = Math.round(c.dir > 0 ? lerp(40, HOUSE.x0, v) : lerp(HOUSE.x0, 40, v)), y = QUAY + 2;
    R(x - 2, y, 5, 2, '#1e1a22'); R(x - 1, y - 1, 3, 1, '#2a2630');
    R(c.dir > 0 ? x + 2 : x - 2, y, 1, 1, '#fff2c8'); R(c.dir > 0 ? x - 2 : x + 2, y + 1, 1, 1, '#ff4a3a');
    halo(HL.qlamp, c.dir > 0 ? x + 4 : x - 4, y, 0.7);
  }
}
const WINS = [];
function layoutWindows() {
  const r = rng(404);
  for (const b of BLOCKS) for (let n = 0; n < 3; n++) {
    const floors = Math.floor((b.base - b.top - 3) / b.st);
    const f = Math.floor(r() * floors), i = b.x + 2 + 3 * Math.floor(r() * Math.max(1, Math.floor((b.w - 4) / 3)));
    const y = b.top + 3 + f * b.st;
    if (floors < 1 || y + 2 >= b.base || i >= b.x + b.w - 2 || r() < 0.25) continue;
    WINS.push({ x: i, y, h: b.ti === 2 ? 3 : 2, on: mixHex(WARMW[r() < 0.7 ? 0 : 1], '#40202a', b.dk * 0.45), off: mixHex('#1c1626', CNIGHT, b.dk * 0.5), k: 20 + Math.floor(r() * 3) * 10, s: r() });
  }
}
function drawWindows(t) {
  for (const w of WINS) R(w.x, w.y, 1, w.h, hash(w.x, step(t + w.s * 12, w.k), w.y) < 0.55 ? w.on : w.off);
}
// people walking along the bastions' top, tiny at this distance
const TINY = [{ n: 2, ph: 0.05, dir: 1, c: '#e8e2d4' }, { n: 2, ph: 0.3, dir: -1, c: '#c8404a' }, { n: 3, ph: 0.55, dir: 1, c: '#3a64c0' },
  { n: 2, ph: 0.8, dir: -1, c: '#e0b840' }, { n: 3, ph: 0.15, dir: -1, c: '#2a9a8a' }, { n: 2, ph: 0.68, dir: 1, c: '#d87aa0' }];
function drawTiny(t, crowd) {
  TINY.forEach((p, i) => {
    if (rk(i + 20) >= crowd * 1.6) return;
    const u = frac(p.n * t / LOOP + p.ph), x = Math.round(p.dir > 0 ? lerp(46, 316, u) : lerp(316, 46, u)), bob = step(t, 768) % 2;
    R(x, BASTION - 4 + bob, 1, 1, '#e8c8a0'); R(x, BASTION - 3 + bob, 1, 2, p.c); R(x + (bob ? 1 : 0), BASTION - 1, 1, 1, '#2a2226');
  });
}
const bellGlow = (t, list) => list.reduce((a, b) => Math.max(a, t >= b && t < b + 2.2 ? 1 - (t - b) / 2.2 : 0), 0);

/* ================= Glints: the moon's path and the lamps' sparkle on the water ================= */
function drawGlints(t) {
  const fr = Math.floor(t * 60);
  ctx.fillStyle = '#e8e8ff';
  for (let k = 0; k < 26; k++) {                                // the moon's broken path (seen between the boats)
    const h1 = hash(k, fr, 7);
    if (h1 < 0.45) continue;
    const y = MANOEL.wl + 2 + k, x = MOON.x + Math.round((hash(k, fr, 8) - 0.5) * (4 + k * 0.5));
    ctx.globalAlpha = 0.35 + 0.3 * h1; ctx.fillRect(x, y, 1 + (h1 > 0.85 ? 1 : 0), 1);
  }
  ctx.fillStyle = '#ffd890';
  for (let k = 0; k < 160; k++) {                               // gold sparkle on the bastions' mirror and under the lamps
    const q = QLAMPS[k % QLAMPS.length], h1 = hash(k, fr, 9);
    if (h1 < 0.45) continue;
    ctx.globalAlpha = 0.22 + 0.35 * h1;
    ctx.fillRect(q + Math.round((hash(k, fr, 10) - 0.5) * 18), WL + 2 + Math.floor(hash(k, fr, 11) * (KERB - WL - 6)), 2, 1);
  }
  ctx.fillStyle = '#8aa0d0';
  for (let k = 0; k < 90; k++) {                                // cool glints on the open water
    const h1 = hash(k, fr, 12);
    if (h1 < 0.5) continue;
    ctx.globalAlpha = 0.2 + 0.25 * h1;
    ctx.fillRect(Math.floor(hash(k, fr, 13) * HOUSE.x0), WL + 4 + Math.floor(hash(k, fr, 14) * (KERB - WL - 8)), 2, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Valletta's own weather: bougainvillea bracts on the breeze, three depths ================= */
// papery magenta bracts loosed from the townhouse's bougainvillea and the gardens, drifting left over the harbour
const BRACTS = [
  { n: 16, cnt: 18, y0: 100, y1: 190, size: 1, a: 0.55, k: 3 },
  { n: 22, cnt: 12, y0: 120, y1: 230, size: 1, a: 0.8, k: 5 },
  { n: 30, cnt: 7, y0: 130, y1: 270, size: 2, a: 0.95, k: 7 },
];
const BRACT_COLS = ['#f070b0', '#d03c8c', '#a82a72'];
function drawBracts(depth, t) {
  const L = BRACTS[depth];
  for (let i = 0; i < L.cnt; i++) {
    const { cyc, u } = cycle(t, L.n, hash(i, depth, 41));
    const x0 = 120 + hash(i, cyc, 42 + depth) * 380, x = Math.round(x0 - u * (180 + depth * 60) + 4 * Math.sin(u * L.k + i));
    const y = Math.round(lerp(L.y0, L.y1, u) + 3 * Math.sin(u * L.k * 2 + i * 1.3));
    const col = BRACT_COLS[(i + cyc) % 3], spin = step(t + i * 0.2, 960) % 2;
    ctx.globalAlpha = L.a * Math.min(1, u * 8, (1 - u) * 8);
    if (L.size > 1) { R(x, y, 2, 1, col); R(x + spin, y + 1, 1, 1, '#f8a0c8'); } else R(x, y, 1, 1, col);
  }
  ctx.globalAlpha = 1;
}

/* ================= The cast: people who stay, each with a verb on the loop =================
   rank: drawn while rank < crowd (Settings, People); rank 0 is there at every level above 0 (the fisherman, the couple,
   the seller, the friends at the café, the ferry's passengers, the luzzu's skipper); couples share one rank */
const rk = i => frac((i + 0.5) * 0.618);
const FISHER = { x: 84, y: KERB + 1 };
const CAST = [
  { id: 'fisher', x: FISHER.x, y: FISHER.y, rank: 0, P: look(19, 1, 5, '#4a5a7a', BOTS[1], { hat: '#2a2a34' }) },
  { id: 'him', x: 238, y: FEET, rank: 0, P: look(21, 1, 0, '#e8e2d4', BOTS[0]) },
  { id: 'her', x: 245, y: FEET, rank: 0, P: look(19, 0, 2, '#c8404a', BOTS[1], { long: 4 }) },
  { id: 'photo', x: 284, y: FEET, rank: 0.3, P: look(20, 2, 0, '#2a9a8a', BOTS[2]) },
  { id: 'seller', x: 405, y: FEET - 1, rank: 0, P: look(19, 1, 0, '#e8e2d4', BOTS[1], { apron: '#f2ece0', dk: 0.05 }) },
  { id: 'tea1', x: 338, y: 229, rank: 0, P: look(21, 2, 5, '#3a64c0', BOTS[0]) },
  { id: 'tea2', x: 354, y: 229, rank: 0, P: look(20, 0, 1, '#d87aa0', BOTS[3], { long: 3 }) },
  { id: 'reader', x: 372, y: 226, rank: 0.7, P: look(21, 1, 5, '#c0c8d8', BOTS[2]) },
  { id: 'bench', x: 216, y: 236, rank: 0.4, P: look(22, 0, 3, '#7a5ac0', BOTS[0]) },
];
// the fisherman's rod: its tip, and where it is in his catch (every 40 s: reel in, the fish, into the bucket, cast)
function fishing(t) {
  const c = CATCHES.find(c => t >= c && t < c + 8), r = c === undefined ? -1 : t - c;
  const bob = Math.round(wave(t, 80) * 0.6 + (hash(3, step(t, 240)) < 0.15 ? 1 : 0));
  const lift = r >= 0 && r < 5 ? Math.round(Math.min(1, r / 1.5) * 4) : r >= 5 && r < 6.5 ? 2 : 0;
  return { r, tipX: FISHER.x + 25, tipY: FISHER.y - 30 - lift + bob };
}
function castPose(c, t) {
  const fw = fwOn(t);
  switch (c.id) {
    case 'fisher': { const f = fishing(t); return { sit: true, view: 0, aR: 3, aL: f.r >= 5 && f.r < 6.5 ? 3 : 0 }; }
    case 'him': { const kiss = frac((t + 20) / 40) > 0.88; return kiss ? { view: 2, dir: 1 } : { view: 0, aR: fw && frac(t / 9) < 0.4 ? 2 : 0 }; }
    case 'her': { const kiss = frac((t + 20) / 40) > 0.88; return kiss ? { view: 2, dir: -1, lean: -1 } : { view: 0, lean: -1, aL: fw ? 2 : 0 }; }
    case 'photo': return frac((t + 5) / 24) < 0.62 || fw ? { view: 0, aR: 2 } : { view: 2, dir: -1 };
    case 'seller': return { view: 1, aR: serving(t) ? 3 : 0 };
    case 'tea1': { const g = step(t, 40) % 3; return { sit: true, view: 2, dir: 1, aR: g === 0 ? 5 : g === 1 ? 3 : 0 }; }
    case 'tea2': { const g = (step(t, 40) + 1) % 3; return { sit: true, view: 2, dir: -1, aR: g === 0 ? 3 : g === 2 ? 5 : 0 }; }
    case 'reader': return { sit: true, view: 1 };
    case 'bench': return { sit: true, view: 0, aR: frac(t / 15) < 0.2 ? 5 : 0 };
  }
  return {};
}
function drawCastMember(c, t) {
  const o = castPose(c, t);
  if (c.id === 'fisher') {                                      // the rod over the water, the line, the fish, the bucket
    const f = fishing(t);
    ctx.fillStyle = '#b89870';
    for (let k = 0; k <= 22; k++) ctx.fillRect(Math.round(lerp(c.x + 3, f.tipX, k / 22)), Math.round(lerp(c.y - 9, f.tipY, k / 22)), 1, 1);
    const lineEnd = f.r >= 0 && f.r < 5 ? lerp(KERB - 6, f.tipY + 5, Math.min(1, f.r / 2.5)) : KERB - 6;
    if (f.r >= 6.5 && f.r < 7.4) {                               // the cast: the line arcs out
      const u = (f.r - 6.5) / 0.9;
      ctx.fillStyle = 'rgba(220,220,235,0.5)';
      for (let k = 0; k < 10; k++) { const v = k / 9 * u; ctx.fillRect(Math.round(f.tipX + v * 14), Math.round(f.tipY - 6 * Math.sin(Math.PI * v) + v * 16), 1, 1); }
    } else {
      ctx.fillStyle = 'rgba(230,230,245,0.55)';
      for (let y = f.tipY + 1; y < lineEnd; y++) ctx.fillRect(f.tipX, y, 1, 1);
    }
    if (f.r >= 2.5 && f.r < 5) { const w = step(t, 1440) % 2; R(f.tipX - w, Math.round(lineEnd), 1, 3, '#d8e0ec'); R(f.tipX + 1 - w, Math.round(lineEnd) + 1, 1, 1, '#a8b4c8'); }
    if (f.r >= 7.2 && f.r < 8) { const u = (f.r - 7.2) / 0.8; ctx.globalAlpha = 0.6 * (1 - u); R(f.tipX + 13 - Math.round(u * 3), KERB - 7, 3 + Math.round(u * 6), 1, '#c8d4e8'); ctx.globalAlpha = 1; }
    R(c.x - 9, c.y - 5, 5, 5, '#3a5a8a'); R(c.x - 9, c.y - 5, 5, 1, '#5a7aaa');
  }
  if (c.id === 'reader') {
    const turn = frac(t / 20) < 0.05;
    R(c.x - (turn ? 4 : 2), c.y - 7, turn ? 9 : 5, 3, '#e8e0cc'); R(c.x, c.y - 7, 1, 3, '#a8a090');
  }
  person(c.x, c.y, c.P, o);
  if (c.id === 'seller') {                                       // he stands behind the glass counter of pastizzi
    R(SHOP.x0 + 3, 194, SHOP.x1 - SHOP.x0 - 5, 1, '#fff4d8'); R(SHOP.x0 + 3, 195, SHOP.x1 - SHOP.x0 - 5, 3, '#f8e8c8');
    for (let x = SHOP.x0 + 4; x < SHOP.x1 - 2; x += 3) R(x, 196, 2, 1, '#e0a040');
    R(SHOP.x0 + 3, 198, SHOP.x1 - SHOP.x0 - 5, HOUSE.floor - 199, '#8a5a38');
    if (o.aR === 3) R(c.x + 5, 193, 2, 2, '#f4ecd8');            // the paper bag, handed over
  }
  if (c.id === 'tea2') { R(344, 222, 1, 2, '#e8a050'); R(348, 222, 1, 2, '#e8a050'); R(346, 222, 1, 1, '#f4e8d0'); }
  if (c.id === 'tea1' && step(t, 120) % 2) { ctx.globalAlpha = 0.35; R(344, 219, 1, 2, '#e8e8f0'); ctx.globalAlpha = 1; }   // the tea's steam
}

/* ================= Scripted people: the ferry's passengers, the skipper, the pastizzeria's queue ================= */
// keys [t, x, feetY, alpha]: between two keys they walk if they move, otherwise they stand
function scriptAt(keys, t) {
  if (t < keys[0][0] || t >= keys[keys.length - 1][0]) return null;
  let k = 0; while (keys[k + 1][0] <= t) k++;
  const [t0, x0, y0, a0] = keys[k], [t1, x1, y1, a1] = keys[k + 1], u = (t - t0) / (t1 - t0);
  return { x: lerp(x0, x1, u), y: lerp(y0, y1, u), a: lerp(a0, a1, u), moving: x0 !== x1 || y0 !== y1, dir: x1 > x0 ? 1 : x1 < x0 ? -1 : 0, down: y1 > y0 + 4 };
}
// a walk along points at a speed, with optional waits ([x, y, wait]); fades in from the first point if asked
function route(t0, pts, speed = 22, fadeIn = false, fadeOut = false) {
  const keys = [[t0, pts[0][0], pts[0][1], fadeIn ? 0 : 1]];
  let t = t0;
  for (let i = 1; i < pts.length; i++) {
    const [x, y, wait = 0] = pts[i], [px0, py0] = pts[i - 1];
    t += Math.max(0.6, Math.hypot(x - px0, y - py0) / speed);
    keys.push([t, x, y, 1]);
    if (wait) { t += wait; keys.push([t, x, y, 1]); }
  }
  if (fadeOut) keys[keys.length - 1][3] = 0;
  return keys;
}
const pLook = k => look(19 + (k % 3), k % 4, (k * 3 + 1) % 6, TOPS[(k * 5 + 2) % 12], BOTS[(k * 7) % 6], { long: k % 3 === 1 ? 3 : 0 });
const SCRIPTS = [
  // two who wait for the ferry at the pontoon and board it
  { P: pLook(1), rank: 0, keys: [[14, -12, 207, 1], [21, 141, 205, 1], [64, 141, 205, 1], [66, 151, 210, 0]], face: 0 },
  { P: pLook(2), rank: 0, keys: [[16, -12, 207, 1], [23, 148, 205, 1], [64.6, 148, 205, 1], [66.6, 154, 210, 0]], face: 0, phone: true },
  // four who come off it and walk away
  { P: pLook(3), rank: 0, keys: route(45, [[152, 210], [152, 205], [-12, 207]], 22, true) },
  { P: pLook(4), rank: 0, keys: route(47.5, [[160, 210], [160, 205], [380, 207], [398, 272]], 22, true) },
  { P: pLook(5), rank: 0.3, keys: route(50, [[156, 210], [156, 205], [-12, 207]], 20, true) },
  { P: pLook(6), rank: 0.2, keys: route(52.5, [[164, 210], [164, 205], [380, 207], [398, 272]], 24, true) },
  // the luzzu's skipper: walks up and steps aboard; home again, steps off and walks away
  { P: look(20, 2, 5, '#3a5a8a', BOTS[1], { hat: '#2a2a34' }), rank: 0, keys: [[109, -12, 207, 1], [123, 316, 207, 1], [125, 318, 211, 0]] },
  { P: look(20, 2, 5, '#3a5a8a', BOTS[1], { hat: '#2a2a34' }), rank: 0, keys: route(223, [[318, 211], [318, 206], [380, 207], [398, 272]], 22, true) },
];
// the queue: a customer is served every 12 s (k = 0..19, served at 12k + 6); keys relative to that moment
const SERVE = 12, QX = [414, 393, 381];
const QKEYS = [[-42, 300, 207, 0], [-39, 330, 207, 1], [-36.6, QX[2], 207, 1], [-25.5, QX[2], 207, 1], [-24, QX[1], 207, 1], [-13.5, QX[1], 207, 1],
  [-12, QX[0], 207, 1], [0, QX[0], 207, 1], [1, 410, 212, 1], [4.2, 400, 272, 1]];
const CUSTOMERS = Array.from({ length: 20 }, (_, k) => ({ k, S: SERVE * k + 6, P: pLook(k + 7), rank: k % 4 === 0 ? 0 : rk(k) }));
const qRel = (t, S) => ((t - S + 120) % LOOP + LOOP) % LOOP - 120;
let curCrowd = 1;
function serving(t) {                                             // the seller hands a bag over in the 2.5 s before each serve
  return CUSTOMERS.some(cu => cu.rank < curCrowd && (r => r > -2.5 && r < 0)(qRel(t, cu.S)));
}

/* ================= Walkers: along the Strand and across the near paving ================= */
const LANE_A = [[-12, 207], [380, 207], [398, 272]], LANE_B = [[-12, 252], [492, 252]], LANE_C = [[-12, 229], [304, 229], [322, 272]];
const laneLen = L => L.slice(1).reduce((a, p, i) => a + Math.hypot(p[0] - L[i][0], p[1] - L[i][1]), 0);
function onLane(L, d) {
  for (let i = 1; i < L.length; i++) {
    const seg = Math.hypot(L[i][0] - L[i - 1][0], L[i][1] - L[i - 1][1]);
    if (d <= seg || i === L.length - 1) { const u = clamp(d / seg, 0, 1); return [lerp(L[i - 1][0], L[i][0], u), lerp(L[i - 1][1], L[i][1], u), L[i][0] > L[i - 1][0] ? 1 : -1, L[i][1] - L[i - 1][1] > 20]; }
    d -= seg;
  }
}
const WALKERS = [
  { lane: LANE_A, n: 4, ph: 0.1, dir: 1, speed: 20, rank: 0.05, P: look(20, 3, 1, '#e0803a', BOTS[0]), dog: true },
  { lane: LANE_A, n: 6, ph: 0.4, dir: -1, speed: 40, rank: 0.33, P: look(20, 0, 3, '#5aa8d0', BOTS[1]), jog: true },
  { lane: LANE_A, n: 3, ph: 0.7, dir: 1, speed: 19, rank: 0.25, P: look(19, 0, 2, '#e8e2d4', BOTS[4]), pair: look(20, 1, 0, '#4a8a4a', BOTS[2]) },
  { lane: LANE_A, n: 4, ph: 0.62, dir: -1, speed: 18, rank: 0.6, P: look(19, 2, 1, '#d87aa0', BOTS[3], { long: 4 }), eat: true },
  { lane: LANE_C, n: 4, ph: 0.35, dir: 1, speed: 16, rank: 0.08, P: look(22, 1, 5, '#5a6a8a', BOTS[4], { hat: '#2a2a34' }), pair: look(21, 0, 5, '#8a2a34', BOTS[1], { skirt: '#3a3a58' }) },
  { lane: LANE_C, n: 5, ph: 0.85, dir: -1, speed: 24, rank: 0.28, P: look(21, 2, 0, '#e0803a', BOTS[0]), kid: look(13, 2, 0, '#5aa8d0', BOTS[3]) },
  { lane: LANE_B, n: 3, ph: 0.2, dir: 1, speed: 18, rank: 0.15, P: look(25, 1, 5, '#8a2a34', BOTS[5], { hat: '#3a3a44' }) },
  { lane: LANE_B, n: 2, ph: 0.65, dir: -1, speed: 21, rank: 0.42, P: look(24, 3, 0, '#e0b840', BOTS[0]), pair: look(23, 0, 4, '#3a64c0', BOTS[1], { long: 4 }) },
];
WALKERS.forEach(w => { w.len = laneLen(w.lane); w.dur = w.len / w.speed; });
function walkerAt(w, t) {
  const P = LOOP / w.n, s = ((t + w.ph * P) % P + P) % P;
  if (s >= w.dur) return null;
  const d = w.dir > 0 ? s * w.speed : w.len - s * w.speed, [x, y, sx, down] = onLane(w.lane, d);
  return { x, y, dir: sx * w.dir, down: down && w.dir > 0, up: down && w.dir < 0 };
}
function drawDog(x, y, dir, t) {
  const st = step(t, 1536) % 2;
  R(x - 3, y - 4, 6, 3, '#6a4a32'); R(dir > 0 ? x + 2 : x - 4, y - 6, 3, 3, '#7a5a3e'); R(dir > 0 ? x + 4 : x - 4, y - 7, 1, 1, '#5a3a26');
  R(x - 3 + st, y - 1, 1, 1, '#3a2a22'); R(x + 1 + st, y - 1, 1, 1, '#3a2a22'); R(dir > 0 ? x - 4 : x + 3, y - 5, 1, 1, '#6a4a32');
}
function drawWalker(w, t, at, crowd) {
  const st = step(t, w.jog ? 1536 : 768) % 2, view = at.down ? 1 : at.up ? 0 : 2;
  person(at.x, at.y, w.P, { view, dir: at.dir, step: st, aR: w.eat ? 5 : w.dog ? 3 : 0 });
  if (w.pair && crowd > w.rank) person(at.x - at.dir * 7, at.y + 1, w.pair, { view, dir: at.dir, step: 1 - st, lean: view === 2 ? 0 : 1 });
  if (w.kid && crowd > w.rank) {                                // a child on a scooter, ahead of the parent
    const kx = Math.round(at.x + at.dir * 10), ky = Math.round(at.y) + 1;
    person(kx, ky - 2, w.kid, { view: 2, dir: at.dir });
    R(kx - 3, ky, 7, 1, '#3a8ad0'); R(kx + at.dir * 3, ky - 8, 1, 8, '#4a4e5c'); R(kx - 3, ky + 1, 1, 1, '#1e1e26'); R(kx + 3, ky + 1, 1, 1, '#1e1e26');
  }
  if (w.dog) {
    const dx = Math.round(at.x + at.dir * 12), dy = Math.round(at.y);
    drawDog(dx, dy, at.dir, t);
    ctx.fillStyle = '#2a2026';
    for (let k = 1; k < 9; k++) ctx.fillRect(Math.round(lerp(at.x + at.dir * 4, dx - at.dir * 2, k / 9)), Math.round(lerp(at.y - 9, dy - 5, k / 9) + Math.sin(Math.PI * k / 9) * 2), 1, 1);
  }
}

/* ================= Animals: the cat on the bollard, pigeons on the paving ================= */
const CAT = { x: 306, y: 190 };                                  // its paws on the bollard's top
function drawCat(t) {
  const { x, y } = CAT, flick = step(t, 32) % 2 && frac(t / 7.5) < 0.3, look = step(t, 16) % 2, up = fwOn(t);
  const c = '#c8803e', d = '#8a5228';
  R(x, y - 3, 4, 3, c); R(x + 1, y - 4, 3, 1, c); R(x + 3, y - 1, 1, 1, d);
  R(look ? x - 1 : x + 2, y - (up ? 7 : 6), 3, 2, c);              // the head, turned now and then, up at the fireworks
  R(look ? x - 1 : x + 2, y - (up ? 8 : 7), 1, 1, d); R(look ? x + 1 : x + 4, y - (up ? 8 : 7), 1, 1, d);
  if (flick) { R(x + 4, y - 3, 1, 1, c); R(x + 5, y - 4, 1, 1, c); } else { R(x + 4, y - 1, 2, 1, c); }
}
const PIGEONS = [[262, 246, 0.1], [270, 249, 0.45], [281, 245, 0.8]];
function drawPigeons(t) {
  for (const [x, y, ph] of PIGEONS) {
    const peck = hash(x, step(t + ph * 3, 480)) < 0.4, dir = hash(y, step(t + ph * 7, 96)) < 0.5 ? 1 : -1;
    R(x - 2, y - 2, 4, 2, '#8a8ca0'); R(x - 2, y - 2, 4, 1, '#a8aac0');
    R(dir > 0 ? x + 2 : x - 3, peck ? y - 1 : y - 3, 1, 1, '#6a6c80');
    R(x - 1, y, 1, 1, '#c87a5a'); R(x + 1, y, 1, 1, '#c87a5a');
  }
}

/* ================= Everything near, in feet order ================= */
function drawNear(t, crowd) {
  const DL = [];
  const put = (y, f) => DL.push([y, f]);
  for (const c of CAST) if (c.rank < crowd) put(c.y, () => drawCastMember(c, t));
  for (const sc of SCRIPTS) {
    if (sc.rank >= crowd) continue;
    const at = scriptAt(sc.keys, t);
    if (!at) continue;
    put(at.y, () => {
      ctx.globalAlpha = at.a;
      if (at.moving) person(at.x, at.y, sc.P, { view: at.down ? 1 : 2, dir: at.dir || 1, step: step(t, 768) % 2 });
      else person(at.x, at.y, sc.P, { view: sc.face ?? 0, aR: sc.phone && frac(t / 16) < 0.5 ? 2 : 0 });
      ctx.globalAlpha = 1;
    });
  }
  for (const cu of CUSTOMERS) {
    if (cu.rank >= crowd) continue;
    const at = scriptAt(QKEYS, qRel(t, cu.S));
    if (!at) continue;
    const atCounter = !at.moving && Math.round(at.x) === QX[0], eating = at.down;
    put(at.y, () => {
      ctx.globalAlpha = at.a;
      person(at.x, at.y, cu.P, at.moving ? { view: eating ? 1 : 2, dir: at.dir || 1, step: step(t, 768) % 2, aR: eating ? 5 : 0 } : atCounter ? { view: 0 } : { view: 2, dir: 1 });
      ctx.globalAlpha = 1;
    });
  }
  for (const w of WALKERS) {
    if (w.rank >= crowd) continue;
    const at = walkerAt(w, t);
    if (at) put(at.y, () => drawWalker(w, t, at, crowd));
  }
  put(CAT.y - 1, () => drawCat(t));
  put(245, () => drawPigeons(t));
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, f] of DL) f();
}

/* ================= Weather: Valletta's own bracts; the shared picks at three depths ================= */
const WEATHER = weatherPlans(['petals']);
const LIGHTS = [{ x: LAMP.x, y: LAMP.y + 4, r: 44 }, { x: 410, y: 192, r: 34 }, { x: 359, y: 210, r: 26 }];
const LIGHTS_BB = [...LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y - 4, r: 16 }];
const WX_LAND = [[100, WL], [WL + 2, KERB], [KERB + 6, H + 4]];
const WX = { land: WX_LAND, lights: LIGHTS.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })), leaves: { wind: [0, 0, W, 196] } };
const WX_BB = { ...WX, lights: LIGHTS_BB.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r })) };

function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.82 && (d - 0.82) * 3.2 > bayer(x, y)) p.put(x, y, '#05060e', 90);
  }
  return p.done();
}

/* ================= Pre-render everything static ================= */
let HL, SPR, SKY_C, MOON_C, WISP_C, RIDGE_C, DOME_C, BACK_C, CITY_C, SPIRE_C, BASTION_C, MANOEL_C, WATER_C,
  STRAND_C, HOUSE_C, BB_C, BB_AD, PITCH, VIG_C;
function prerender() {
  HL = {
    moon: makeHalo(40, '#9ab0ff', 0.2, 1.8), moonCore: makeHalo(14, '#fff2d0', 0.3, 1.5),
    haze: makeHalo(170, '#ff9a4a', 0.07, 1.3), dome: makeHalo(40, '#ffe8b8', 0.18, 1.6), domeTop: makeHalo(14, '#fff2d0', 0.22, 1.4),
    spire: makeHalo(26, '#ffd8a0', 0.16, 1.6), wall: makeHalo(30, '#ffb050', 0.14, 1.5), qlamp: makeHalo(5, '#ffd890', 0.4, 1.3),
    lamp: makeHalo(56, '#ffb060', 0.22, 1.7), lampCore: makeHalo(14, '#ffe6b0', 0.5, 1.4), shop: makeHalo(36, '#ffa850', 0.24, 1.5),
    gal: makeHalo(30, '#ffc070', 0.16, 1.5), bulb: makeHalo(18, '#ffc070', 0.3, 1.4), sheen: makeHalo(70, '#e09a48', 0.09, 1.4),
    ferry: makeHalo(24, '#ffe8b0', 0.18, 1.5), ferryFar: makeHalo(12, '#ffe8b0', 0.2, 1.5), cruise: makeHalo(30, '#ffd890', 0.2, 1.5),
    lantern: makeHalo(8, '#ffc070', 0.4, 1.3), planeL: makeHalo(4, '#ffffff', 0.4, 1.2), bell: makeHalo(18, '#ffe8b0', 0.3, 1.5),
    bbLamp: makeHalo(6, '#ffc878', 0.3, 1.6), bbWash: makeHalo(36, '#ffb870', 0.06, 1.5),
    fw: FW_COLS.map(c => makeHalo(30, c[1], 0.22, 1.6)),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp);
  { const sx = SKY_C.getContext('2d');                           // the haze and the moon's glow never change: bake them
    drawHalo(sx, HL.haze, 210, 150, 1); drawHalo(sx, HL.moon, MOON.x, MOON.y); drawHalo(sx, HL.moonCore, MOON.x, MOON.y); }
  layoutCity(); layoutWindows();
  { const p = painter(W, H); renderLesserDomes(p); renderCampanile(p); RIDGE_C = p.done(); }
  DOME_C = renderDome();
  { const p = painter(W, H); BLOCKS.filter(b => b.ti === 0).forEach(b => drawBlock(p, b)); BACK_C = p.done(); }
  { const p = painter(W, H); renderPaleDome(p); BLOCKS.filter(b => b.ti > 0).forEach(b => drawBlock(p, b)); renderWestBastion(p); renderElmo(p); CITY_C = p.done();
    const cx = CITY_C.getContext('2d');                         // the quay floodlights' glow on the houses over the bastions
    cx.save(); cx.beginPath(); cx.rect(44, 118, HOUSE.x0 - 44, 20); cx.clip();
    for (const x of QLAMPS) drawHalo(cx, HL.wall, x, QUAY - 4, 0.9);
    cx.restore(); }
  SPIRE_C = renderSpire();
  { const p = painter(W, H); renderBastions(p); BASTION_C = p.done(); }
  MANOEL_C = renderManoel(); WATER_C = renderWater();
  const CRUISE_KEY = { ...LUZZU_KEY, k: '#2a2a34', L: '#4a4038', y: '#ffe2a0', h: '#3a2a26', c: '#5a4a5a' };
  SPR = {
    luzN: sprite(LUZZU_ROWS, LUZZU_KEY), luzNR: sprite(LUZZU_ROWS, LUZZU_KEY, true),
    luzM: sprite(LUZZU_MID, LUZZU_KEY), luzMR: sprite(LUZZU_MID, LUZZU_KEY, true),
    ferryN: sprite(FERRY_ROWS, FERRY_KEY), ferryNR: sprite(FERRY_ROWS, FERRY_KEY, true),
    ferryM: sprite(FERRY_MID, FERRY_KEY), ferryMR: sprite(FERRY_MID, FERRY_KEY, true), ferryF: sprite(FERRY_FAR, FERRY_KEY),
    cruiseR: sprite(CRUISE_ROWS, CRUISE_KEY, true), dgh: sprite(DGH_ROWS, LUZZU_KEY),
  };
  STRAND_C = renderStrand(); HOUSE_C = renderHouse();
  BB_C = renderBillboard(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#006994' });
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 6, 0.8);
  ctx.drawImage(BB_C, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, BB.y - 7, 3, 1, '#f2dcaa'); halo(HL.bbLamp, lx, BB.y - 6, 0.9); }
}
// someone crossing the gallarija's lit room twice a loop, behind the panes
const GAL_WALK = [[40, 52], [150, 162]];
function drawHouseLife(t) {
  const sway = Math.round(1.5 * wave(t, 40) + 0.8 * wave(t, 104, 0.3));
  R(458 + Math.max(0, sway), 129, 2, 14, '#f4ead2'); R(458 + Math.max(0, sway), 129, 1, 14, '#d8c8a8');   // the curtain in the breeze
  for (let k = 0; k < 14; k++) {
    const x = HOUSE.x0 - 6 + Math.round(hash(k, 91) * 14 + Math.sin(t * TAU * 30 / LOOP + k) * 1.4), y = 150 + Math.round(hash(k, 92) * 36);
    R(x, y, 1, 1, k % 3 ? '#e85aa8' : '#c83a8a');
  }
}
function drawGallarija(t) {
  const w = GAL_WALK.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const u = (t - w[0]) / (w[1] - w[0]), cx = Math.round(lerp(400, 444, u));
  ctx.save(); ctx.beginPath(); ctx.rect(399, 151, 47, 10); ctx.clip();
  R(cx - 1, 152, 3, 3, '#7a4a2a'); R(cx - 2, 155, 5, 8, '#7a4a2a');
  ctx.restore();
}
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  curCrowd = crowd;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  // sky, stars, moon, wisps, the plane, the fireworks over the city
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8eeff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of WISP_C) drawScroll(L.c, L.y, L.k, t);
  drawPlane(t);
  drawFireworks(t, reducedMotion);
  // Valletta: the ridge's towers, the dome in its glow, the houses and their windows, the spire, the bells
  ctx.drawImage(RIDGE_C, 0, 0);
  const bc = bellGlow(t, BELLS_C), bp = bellGlow(t, BELLS_P);
  if (bc > 0) halo(HL.bell, CAMP.cx, CAMP.top + 8, bc);
  halo(HL.dome, DOME.cx, 74, 1); halo(HL.domeTop, DOME.cx, 50, 0.8 + 0.1 * wave(t, 12));
  ctx.drawImage(DOME_C, 0, 0);
  ctx.drawImage(BACK_C, 0, 0);
  ctx.drawImage(CITY_C, 0, 0);
  drawWindows(t);
  drawTiny(t, crowd);
  halo(HL.spire, SPIRE.cx, 96, 0.9);
  ctx.drawImage(SPIRE_C, 0, 0);
  if (bp > 0) { halo(HL.bell, SPIRE.cx, 81, bp); R(SPIRE.cx, 80, 1, 2, '#e8b464'); }
  if (bc > 0.3) R(CAMP.cx, CAMP.top + 7, 1, 2, '#e8b464');
  drawFlock(t);
  drawGulls(t);
  if (wx.petals) drawBracts(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the bastions, their lamps, the cars on the quay road
  ctx.drawImage(BASTION_C, 0, 0);
  for (const x of QLAMPS) { R(x, QUAY - 3, 1, 3, '#2a2228'); R(x, QUAY - 4, 1, 1, '#fff0c0'); halo(HL.qlamp, x, QUAY - 4, 0.9); }
  drawCars(t);
  // Marsamxett: base, the mirror, Manoel Island and its mirror, streaks and glints, then the boats far to near
  ctx.drawImage(WATER_C, 0, WL);
  reflect(ctx, t, { top: WL, rows: KERB - WL, squash: 1.2, alpha: 0.42, amp: 0.9, grow: 0.06, k1: 120, k2: 720 });
  halo(HL.sheen, 120, WL + 10, 0.7); halo(HL.sheen, 250, WL + 10, 0.7);
  ctx.drawImage(MANOEL_C, 0, 0);
  reflect(ctx, t, { top: MANOEL.wl + 1, rows: KERB - MANOEL.wl - 1, squash: 1.2, alpha: 0.4, amp: 0.9, grow: 0.06, k1: 120, k2: 720, x: MANOEL.x0, w: HOUSE.x0 - MANOEL.x0 });
  QLAMPS.forEach((x, i) => streak(ctx, t, x, 3 + (i % 3), '#ffc468', 0.7, x * 0.7, WL + 1, KERB - 1));
  streak(ctx, t, DOME.cx, 8, '#ffe2a8', 0.16, 3, WL + 4, KERB);
  streak(ctx, t, SPIRE.cx, 5, '#ffd090', 0.14, 5, WL + 4, KERB);
  drawGlints(t);
  drawFireworkGlints(t);
  const f = ferryAt(t), lz = luzzuAt(t), boats = [];
  boats.push([ferryPos(f.u).y, () => drawFerry(t, f)]);
  if (lz) boats.push([lz.y, () => drawLuzzu(t, lz, 1)]);
  boats.push([LUZZI[0].y + 9, () => drawLuzzu(t, { x: LUZZI[0].x + 16, y: LUZZI[0].y + 9, face: -1, moored: true }, 0)]);
  boats.push([CRUISE.y, () => drawCruise(t)]);
  boats.push([DGH.y, () => drawDghajsa(t)]);
  boats.sort((a, b) => a[0] - b[0]);
  for (const [, d] of boats) d();
  if (wx.petals) drawBracts(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the Strand, the townhouse, its sign, the people, the lamp
  ctx.drawImage(STRAND_C, 0, 0);
  ctx.drawImage(HOUSE_C, 0, 0);
  drawGallarija(t);
  drawHouseLife(t);
  halo(HL.gal, 422, 158, 0.85 + 0.1 * wave(t, 24)); halo(HL.shop, (SHOP.x0 + SHOP.x1) >> 1, 196, 1);
  for (const tx of [346, 372]) halo(HL.bulb, tx, 210, 0.9 + 0.1 * hash(tx, step(t, 1200)));
  if (billboard) drawBillboard(t, pitch);
  streak(ctx, t, LAMP.x, 6, '#ffc070', 0.22, 3, KERB + 30, H);
  drawNear(t, crowd);
  halo(HL.lamp, LAMP.x, LAMP.y + 2, 1); halo(HL.lampCore, LAMP.x, LAMP.y, 0.9);
  if (wx.petals) drawBracts(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const CROWD = 0.5;
export default {
  id: 'valletta',
  name: 'Valletta',
  tagline: 'Gold walls over Marsamxett',
  timeZone: 'Europe/Malta',
  accent: '#006994',
  focusX: 236,
  landmark: [CAMP.cx - CAMP.hw - 2, SPIRE.cx + SPIRE.hw + 2],   // the Carmelite campanile and dome to the Pro-Cathedral's tower
  landmarkTop: DOME.tip,                                         // the dome's cross and the spire's tip, level
  billboard: BB,
  crowd: CROWD,
  spots: [                                                       // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: DOME.cx - DOME.hw - 1, y: DOME.tip, w: 2 * DOME.hw + 3, h: DOME.base - DOME.tip + 1 },     // the Carmelite dome
    { kind: 'bell', x: SPIRE.cx - SPIRE.hw - 1, y: SPIRE.tip, w: 2 * SPIRE.hw + 3, h: SPIRE.foot - SPIRE.tip + 1 },   // the Pro-Cathedral's tower
    { kind: 'water', x: 236, y: 168, w: 60, h: 14 },                                                                  // Marsamxett
    { kind: 'cat', x: CAT.x - 1, y: CAT.y - 8, w: 7, h: 8 },                                                           // the cat on the bollard
  ],
  ambience: { water: 0.5, waves: 0.18, crowd: 0.2, wind: 0.12, traffic: 0.1, crickets: 0.12 },
  events: [
    { t: 6, type: 'boat', dur: 34, pan: [0.25, -0.35], gain: 0.5 }, { t: 38, type: 'horn', x: FERRY_S.x },
    { t: 72, type: 'horn', x: FERRY_S.x, gain: 0.7 }, { t: 73, type: 'boat', dur: 34, pan: [-0.35, 0.25], gain: 0.5 },
    { t: 44, type: 'boat', dur: 40, pan: [-1, 1], gain: 0.35 },
    ...BELLS_C.map(t => ({ t, type: 'bell', x: CAMP.cx })), ...BELLS_P.map(t => ({ t, type: 'bell', x: SPIRE.cx, gain: 0.7 })),
    { t: 28, type: 'gull', x: 120 }, { t: 114, type: 'gull', x: 360 }, { t: 224, type: 'gull', x: 140 },
    { t: 128, type: 'boat', dur: 22, pan: [0.4, 0.8], gain: 0.45 }, { t: 200, type: 'boat', dur: 22, pan: [0.8, 0.4], gain: 0.45 },
    { t: 160, type: 'splash', x: 260, gain: 0.2 },
    ...CATCHES.map(c => ({ t: c + 7.2, type: 'splash', x: FISHER.x + 38, gain: 0.25 })),
    ...BURSTS.map(b => ({ t: b.t, type: 'firework', x: b.x })),
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
